//import external libraries
import React, {
  useState,
  useReducer,
  useContext,
  useEffect,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";

//import internal services and resources
import Calculation from "./subforms/Calculation";
import Fan from "./subforms/Fan";
import Filtering from "./subforms/Filtering";
import OperatingPoint from "./subforms/OperatingPoint";
import ProjectInformation from "./subforms/ProjectInformation";
import TypeOfUnit from "./subforms/TypeOfUnit";
import HeatRecovery from "./subforms/HeatRecovery";
import {
  projectItem as createProjectItem,
  createProjectItemBlob,
  getProjectItem,
  editProjectItem,
  getAirFlowCalculation,
  getProject,
  getProjectLocationConditions,
  getDamperData,
  //validateProjectItemFan,
  //validateProjectItemRec,
  //CheckBlobExist,
  //  validateRecAsync,
  // validateFanAsync,
} from "../../utils/services/projectService";
import {
  validateRecAsync,
  validateFanAsync,
  validateCoilAsync,
} from "../../utils/services/dllService";
import {
  calculateDewPoint,
  calculateWetBulbTemperature,
  findHumidityRatio,
} from "../../utils/calculationFunctions";
import { appCtx } from "../../utils/hooks/appContext";
import { setSnackbar } from "../../actions/snackbarActions";
import { etyleneGlycol, propyleneGlycol } from "../../utils/glycolConstants";
//import MUI components
import Backdrop from "@mui/material/Backdrop";
import Button from "@mui/material/Button";
import Popover from "@mui/material/Popover";
import CssBaseline from "@mui/material/CssBaseline";
import MuiDrawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ListItem from "@mui/material/ListItem";
import CalculateIcon from "@mui/icons-material/Calculate";
import BlurLinearIcon from "@mui/icons-material/BlurLinear";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import LibraryAddCheckIcon from "@mui/icons-material/LibraryAddCheck";
import EngineeringIcon from "@mui/icons-material/Engineering";
import HeatPumpIcon from "@mui/icons-material/HeatPump";
import styled from "@mui/material/styles/styled";
import useTheme from "@mui/material/styles/useTheme";
import useMediaQuery from "@mui/material/useMediaQuery";
import OperatingPoint1 from "./subforms/OperatingPoint1";
import { themeCtx } from "../../utils/hooks/themeContext";
import Coils from "./subforms/Coils";
// import CoilsIcon from "./subforms/flexibility.png";
import AnimationIcon from "@mui/icons-material/Animation";

//Stilizovanje drawer navigacije
const drawerWidth = 240;

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  "& .MuiDrawer-paper": {
    position: "relative",
    whiteSpace: "nowrap",
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    boxSizing: "border-box",
    ...(!open && {
      overflowX: "hidden",
      transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      width: theme.spacing(7),
      [theme.breakpoints.up("sm")]: {
        width: theme.spacing(9),
      },
    }),
  },
}));

const mainFormReducer = (state, action) => {
  switch (action.type) {
    case "setProjectId":
      return { ...state, projId: action.payload };
    case "updateProject":
      return {
        ...state,
        [action.payload.name]: action.payload.value,
      };
    case "updateSummerConditions": {
      let wetBulbTemperature = calculateWetBulbTemperature(
        state.summerOutdoorTemperature,
        state.summerOutdoorHumidity
      );
      let dewPointTemperature = calculateDewPoint(
        +state.summerOutdoorTemperature,
        +state.summerOutdoorHumidity
      );
      return {
        ...state,
        summerWetBulbTemperature: wetBulbTemperature,
        summerDewPointTemperature: dewPointTemperature,
      };
    }
    case "updateWinterConditions": {
      let humidityRatio = findHumidityRatio(
        +state.winterOutdoorTemperature,
        +state.winterOutdoorHumidity,
        +state.elevation
      );

      return {
        ...state,
        humidityRatio: humidityRatio,
      };
    }

    case "updateOperationPoint":
      return {
        ...state,
        projId: action.payload.id,
        summerOutdoorTemperature: action.payload.data.summerOutdoorTemperature,
        summerWetBulbTemperature: action.payload.data.summerWetBulbTemperature,
        summerDewPointTemperature:
          action.payload.data.summerDewPointTemperature,
        summerOutdoorHumidity: action.payload.data.summerOutdoorHumidity,
        winterOutdoorTemperature: action.payload.data.winterOutdoorTemperature,
        winterOutdoorHumidity: action.payload.data.winterOutdoorHumidity,
        winterOutdoorHumidityRatio:
          action.payload.data.winterOutdoorHumidityRatio,
        elevation: action.payload.data.elevation,
      };
    case "updateBuildingType":
      let airChangeValue;

      if (action.payload.value === "None") airChangeValue = 0;
      else
        airChangeValue = action.payload.value.substring(
          action.payload.value.lastIndexOf("-") + 1,
          action.payload.value.lastIndexOf(")")
        );

      let flowSupply = Math.round(airChangeValue * state.height * state.area);
      let flowReturn = Math.round(airChangeValue * state.height * state.area);

      return {
        ...state,
        [action.payload.name]: action.payload.value,
        changeRate: airChangeValue,
        flowSupply: flowSupply,
        flowReturn: flowReturn,
      };
    case "uploadDataForEdit":
      return {
        ...state,
        id: action.payload.id,
        projId: action.payload.projectId,
        projIdent: action.payload.ident,
        projName: action.payload.name,
        projNumber: action.payload.number,
        revisionNumber: action.payload.revisionNumber,
        area: action.payload.area,
        height: action.payload.height,
        buildingType: action.payload.buildingType,
        changeRate: action.payload.changeRate,
        dropSupply: action.payload.dropSupply,
        flowSupply: action.payload.flowSupply,
        flowReturn: action.payload.flowReturn,
        dropReturn: action.payload.dropReturn,
        // Requirements (moved to Type of Unit)
        ecoDesign: action.payload.ecoDesign,
        casingQuality: action.payload.casingQuality,
        // Type of unit
        installation: action.payload.installation,
        unitType: action.payload.unitType,
        coils: action.payload.coils,
        heatRecovery: action.payload.heatRecovery,
        heatRecoveryPlateType:
          action.payload.sizeOfUnit <= 5
            ? "Rotary"
            : action.payload.sizeOfUnit > 10
            ? "Enthalpy"
            : "Plate",
        controls: action.payload.controls,
        unitDivision: action.payload.unitDivision,
        sizeOfUnit: action.payload.sizeOfUnit,

        airflowDirection: action.payload.airflowDirection,

        // Filtering
        doubleStageFiltering: action.payload.doubleStageFiltering,
        filteringAdvancedSettings: action.payload.filteringAdvancedSettings,
        fineFilterType: action.payload.fineFilterType,
        fineFilterClassSupply: action.payload.fineFilterClassSupply,
        fineFilterClassReturn: action.payload.fineFilterClassReturn,
        // Fan
        fanAdvancedSettings: action.payload.fanAdvancedSettings,
        fanType: action.payload.fanType,
        //Summer conditions
        summerOutdoorTemperature: action.payload.summerOutdoorTemperature,
        summerWetBulbTemperature: action.payload.summerWetBulbTemperature,
        summerDewPointTemperature: action.payload.summerDewPointTemperature,
        summerOutdoorHumidity: action.payload.summerOutdoorHumidity,
        summerIndoorTemperature: action.payload.summerIndoorTemperature,
        summerIndoorHumidity: action.payload.summerIndoorHumidity,
        summerSupplyTemperature: action.payload.summerSupplyTemperature,
        //Cooler
        coolerType: action.payload.coolerType,
        coolerConcentration: action.payload.coolerConcentration,
        coolerTemperatureIn: action.payload.coolerTemperatureIn,
        coolerAirInputParam: action.payload.coolerAirInputParam,
        coolerAirInputParamValue: action.payload.coolerAirInputParamValue,
        coolerWaterInputParam: action.payload.coolerWaterInputParam,
        coolerWaterInputParamValue: action.payload.coolerWaterInputParamValue,
        //Winter conditions
        winterOutdoorTemperature: action.payload.winterOutdoorTemperature,
        winterOutdoorHumidityRatio: action.payload.winterOutdoorHumidityRatio,
        elevation: action.payload.elevation,
        winterOutdoorHumidity: action.payload.winterOutdoorHumidity,
        winterIndoorTemperature: action.payload.winterIndoorTemperature,
        winterIndoorHumidity: action.payload.winterIndoorHumidity,
        //Heater
        heaterType: action.payload.heaterType,
        heaterConcentration: action.payload.heaterConcentration,
        heaterTemperatureIn: action.payload.heaterTemperatureIn,
        heaterAirInputParam: action.payload.heaterAirInputParam,
        heaterAirInputParamValue: action.payload.heaterAirInputParamValue,
        heaterWaterInputParam: action.payload.heaterWaterInputParam,
        heaterWaterInputParamValue: action.payload.heaterWaterInputParamValue,
      };
    case "updateFlowByArea": {
      let flowSupply = Math.round(
        action.payload.value * state.height * state.changeRate
      );
      let flowReturn = Math.round(
        action.payload.value * state.height * state.changeRate
      );

      return {
        ...state,
        [action.payload.name]: action.payload.value,
        flowSupply: flowSupply,
        flowReturn: flowReturn,
      };
    }
    case "updateFlowByHeight": {
      let flowSupply = Math.round(
        action.payload.value * state.area * state.changeRate
      );
      let flowReturn = Math.round(
        action.payload.value * state.area * state.changeRate
      );

      return {
        ...state,
        [action.payload.name]: +action.payload.value,
        flowSupply: flowSupply,
        flowReturn: flowReturn,
      };
    }
    case "updateFlowByChangeRate": {
      let flowSupply = Math.round(
        action.payload.value * state.height * state.area
      );
      let flowReturn = Math.round(
        action.payload.value * state.height * state.area
      );

      return {
        ...state,
        [action.payload.name]: action.payload.value,
        flowSupply: flowSupply,
        flowReturn: flowReturn,
      };
    }
    case "resetUnitSize": {
      return {
        ...state,
        [action.payload.name]: action.payload.value,
        sizeOfUnit: null,
      };
    }
    case "resetToInitialSummer": {
      return {
        ...state,
        summerOutdoorTemperature: action.payload.data.summerOutdoorTemperature,
        summerWetBulbTemperature: action.payload.data.summerWetBulbTemperature,
        summerDewPointTemperature:
          action.payload.data.summerDewPointTemperature,
        summerOutdoorHumidity: action.payload.data.summerOutdoorHumidity,
      };
    }
    case "resetToInitialWinter": {
      return {
        ...state,
        winterOutdoorTemperature: action.payload.data.winterOutdoorTemperature,
        winterOutdoorHumidity: action.payload.data.winterOutdoorHumidity,
        winterOutdoorHumidityRatio: action.payload.data.relativeHumidityWinter,
        elevation: action.payload.data.elevation,
      };
    }
    case "resetToTemperature": {
      return {
        ...state,
        winterOutdoorTemperature: action.payload.data.winterOutdoorTemperature,
        winterOutdoorHumidity: action.payload.data.winterOutdoorHumidity,
        winterOutdoorHumidityRatio: action.payload.data.relativeHumidityWinter,
        elevation: action.payload.data.elevation,
      };
    }
    case "updateInitialSizeOfUnitHeatRecovery": {
      let newSizeOfUnit;

      // Handle locked heat recovery values (Energy:Prime and Aqua:Prime)
      if (
        action.payload.value ===
          "Polypropylene with indirect adiabatic cooling" ||
        action.payload.value === "Polypropylene"
      ) {
        // For locked states, don't change sizeOfUnit, just update the heatRecovery value
        return {
          ...state,
          [action.payload.name]: action.payload.value,
        };
      }

      if (
        action.payload.value === "Rotary heat exchanger" ||
        action.payload.value === "Rotacioni izmenjivač toplote"
      ) {
        const filtered = action.payload.unitTypes?.filter(
          (item) => item.heatRecoveryType === "Rotary"
        );
        newSizeOfUnit = filtered && filtered.length > 0 ? filtered[0] : null;

        return {
          ...state,
          [action.payload.name]: action.payload.value,
          heatRecoveryPlateType: "Rotary",
          sizeOfUnit: newSizeOfUnit?.value || state.sizeOfUnit,
        };
      } else if (
        action.payload.value === "Plate heat exchanger" &&
        state.heatRecoveryPlateType === "Condensing"
      ) {
        const filtered = action.payload.unitTypes?.filter(
          (item) => item.heatRecoveryType === "Plate"
        );
        newSizeOfUnit = filtered && filtered.length > 0 ? filtered[0] : null;
        return {
          ...state,
          [action.payload.name]: action.payload.value,
          heatRecoveryPlateType: "Condensing",
          sizeOfUnit: newSizeOfUnit?.value || state.sizeOfUnit,
        };
      } else {
        const filtered = action.payload.unitTypes?.filter(
          (item) => item.heatRecoveryType === "Enthalpy"
        );
        newSizeOfUnit = filtered && filtered.length > 0 ? filtered[0] : null;
        return {
          ...state,
          [action.payload.name]: action.payload.value,
          heatRecoveryPlateType: "Enthalpy",
          sizeOfUnit: newSizeOfUnit?.value || state.sizeOfUnit,
        };
      }
    }

    case "updateInitialSizeOfUnitHeatRecoveryPlate": {
      let newSizeOfUnit;

      if (action.payload.value === "Enthalpy") {
        const filtered = action.payload.unitTypes?.filter(
          (item) => item.heatRecoveryType === "Enthalpy"
        );
        newSizeOfUnit =
          filtered && filtered.length > 0
            ? filtered[0].value
            : state.sizeOfUnit;
      } else {
        const filtered = action.payload.unitTypes?.filter(
          (item) => item.heatRecoveryType === "Plate"
        );
        newSizeOfUnit =
          filtered && filtered.length > 0
            ? filtered[0].value
            : state.sizeOfUnit;
      }

      return {
        ...state,
        [action.payload.name]: action.payload.value,
        sizeOfUnit: newSizeOfUnit,
      };
    }
    case "sameFilterClasses": {
      if (
        action.payload.name === "filteringAdvancedSettings" // &&
        //action.payload.value === false
      )
        return {
          ...state,
          [action.payload.name]: action.payload.value,
          fineFilterClassReturn: state.fineFilterClassSupply,
        };
      else if (
        action.payload.name === "fineFilterClassSupply" &&
        state.filteringAdvancedSettings === false
      )
        return {
          ...state,
          [action.payload.name]: action.payload.value,
          fineFilterClassReturn: action.payload.value,
        };
      else
        return {
          ...state,
          [action.payload.name]: action.payload.value,
        };
    }
    case "updateUnitDetails": {
      return {
        ...state,
        // [action.payload.name]: action.payload.value,
        fanInstallationHeight: action.payload.details.fanInstallationHeight,
        fanInstallationWidth: action.payload.details.fanInstallationWidth,
        internalHeight: action.payload.details.internalHeight,
        internalWidth: action.payload.details.internalWidth,
        externalWidth: action.payload.details.externalWidth,
        externalHeight: action.payload.details.externalHeight,
        unitFilter: action.payload.details.filter,
        fan: action.payload.details.fan,
        heatRecoverySize: action.payload.details.heatRecoverySize,
        coilsWidth: action.payload.details.coilsWidth,
        coilsHeight: action.payload.details.coilsHeight,
      };
    }
    case "updateDamperDetails": {
      console.log("Updating damper details:", action.payload);
      return {
        ...state,
        damperHeight: action.payload.damperHeight,
        damperWidth: action.payload.damperWidth,
      };
    }
    case "updateDesignData": {
      return {
        ...state,
        // [action.payload.name]: action.payload.value,
        designDryBulbTemperature: action.payload.designDryBulbTemperature,
        designDewPointTemperature: action.payload.designDewPointTemperature,
        designWetBulbTemperature: action.payload.designWetBulbTemperature,
        designWinterOutdoorTemperature:
          action.payload.designWinterOutdoorTemperature,
      };
    }

    case "updateRecData": {
      return {
        ...state,
        // [action.payload.name]: action.payload.value,
        coilInputTemperatureCooler: action.payload.coilInputTemperatureCooler,
        coilInputTemperatureHeater: action.payload.coilInputTemperatureHeater,
        coilInputHumidityCooler: action.payload.coilInputHumidityCooler,
        coilInputHumidityHeater: action.payload.coilInputHumidityHeater,
        summerRecTemperatureOut: action.payload.coilInputTemperatureCooler,
        winterRecTemperatureOut: action.payload.coilInputTemperatureHeater,
        summerRecHumidityOut: action.payload.coilInputHumidityCooler,
        winterRecHumidityOut: action.payload.coilInputHumidityHeater,
        // Store full recVal response for Heat Recovery tab
        summerRecCalculation: action.payload.summerRecCalculation,
        winterRecCalculation: action.payload.winterRecCalculation,
      };
    }
    default:
      return state;
  }
};

function MainForm() {
  const [isEditing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [coilsValSuccess, setCoilsValSuccess] = useState(true);
  const [fanValSuccess, setFanValSuccess] = useState(true);
  const location = useLocation();
  const params = useParams();
  const isSmallScreen = useMediaQuery("(max-width: 600px)");
  const snackbarDispatch = useDispatch();
  const [open, setOpen] = useState(true);
  const [airFlowCalc, setAirFlowCalc] = useState(false);
  const [airTempInCool, setAirTempInCool] = useState(false);
  const [airTempInHeat, setAirTempInHeat] = useState(false);
  const { projectId } = useContext(appCtx);
  const { currentTheme } = useContext(themeCtx);

  const toggleDrawer = () => {
    setOpen(!open);
  };
  const { t } = useTranslation();

  // Map unitType from URL parameter to unit type string
  const getUnitTypeFromParam = () => {
    const unitTypeParam = params?.unitType;
    if (!unitTypeParam) return "Compact: PRO"; // Default fallback

    const unitTypeMap = {
      1: "Compact: PRO",
      2: "Energy: PRO",
      4: "Aqua: PRO",
    };

    return unitTypeMap[unitTypeParam] || "Compact: PRO";
  };

  // Helper function to get coilVal for request payload
  const getCoilVal = (coils, baseUnitType, unitType) => {
    if (baseUnitType === 2 && unitType === "Energy:Prime Standard") {
      return "both"; // coilVal = "both" for Energy:Prime Standard
    }
    // For Energy:Prime Genius and Aqua:Prime Genius, the value is stored as "reversible_refrigerant"
    // This will be sent as-is in the request (backend will handle mapping if needed)
    // For other cases, use the coils value directly
    return coils;
  };

  // Helper function to get recVal for request payload
  const getRecVal = (heatRecovery, baseUnitType) => {
    if (baseUnitType === 2 || baseUnitType === 4) {
      // All locked states (Energy:Prime and Aqua:Prime) send recVal = "PPL_4"
      return "PPL_4";
    }
      if (baseUnitType === 6) {
        // Line:Prime sends recVal = "without"
        return "without";
      }
    // For Compact:Prime, use the heatRecovery value directly
    return heatRecovery;
  };

  const initialState = {
    projId: "",
    projIdent: 123,
    projName: "",
    projNumber: 1,
    revisionNumber: 0,
    area: 100,
    height: 2.8,
    buildingType: "Residential - Basements (3-4)",
    changeRate: 4,
    dropSupply: 250,
    flowSupply: 1120,
    flowReturn: 1120,
    dropReturn: 250,
    // Requirements (moved to Type of Unit)
    ecoDesign: "compliant",
    casingQuality: "XPS",
    // Type of unit
    installation: "",
    unitType: getUnitTypeFromParam(),
    coils: "",
    heatRecovery: "", // "Rotary heat exchanger" bilo
    sizeOfUnit: "",
    controls: t("unitTypeList.controls"),
    unitDivision: "transport3",
    heatRecoveryPlateType: "Rotary", // "Rotary" bilo

    airflowDirection: 0,
    // Operating point
    //summer
    summerOutdoorTemperature: 0,
    summerWetBulbTemperature: 0,
    summerDewPointTemperature: 0,
    summerOutdoorHumidity: 0,
    summerIndoorTemperature: 26,
    summerIndoorHumidity: 50,
    summerSupplyTemperature: 18,
    summerRecTemperatureOut: 0,
    summerRecHumidityOut: 0,
    // Heat Recovery recVal response data
    summerRecCalculation: null,
    winterRecCalculation: null,
    coolerType: "water",
    coolerConcentration: 0,
    coolerTemperatureIn: 7,
    //coolerTemperatureOut: 12,
    designDryBulbTemperature: 34.6,
    designDewPointTemperature: 14.3,
    designWetBulbTemperature: 21.1,
    //winter
    winterOutdoorTemperature: 0,
    winterOutdoorHumidityRatio: 1,
    elevation: 1,
    winterOutdoorHumidity: 0,
    winterIndoorTemperature: 20,
    winterIndoorHumidity: 40,
    winterSupplyTemperature: 22,
    winterRecTemperatureOut: 0,

    heaterType: "water",
    heaterConcentration: 0,
    heaterTemperatureIn: 35,
    //heaterTemperatureOut: 28,

    // Filtering
    doubleStageFiltering: "No",
    filteringAdvancedSettings: false,
    fineFilterType: "Rigid bag filter",
    fineFilterClassSupply: "M6",
    fineFilterClassReturn: "M6",
    // Fan
    fanAdvancedSettings: currentTheme == true ? "On" : "Off",
    fanType: currentTheme == true ? "Premium" : "Standard",
    damperHeight: "211",
    damperWidth: "370",
    fanInstallationHeight: "407",
    fanInstallationWidth: "600",
    internalHeight: "853",
    internalWidth: "600",
    externalWidth: "704",
    externalHeight: "957",
    unitFilter: "Opakfil Basic ST;M6/F7;592x287x296",
    fan: "K3G250PR04H3;ECF(K)6E250-PLHDAL4Y-PRF",
    heatRecoverySize: 0,
    guid: crypto.randomUUID(),
    coilsWidth: "600",
    coilsHeight: "407",
    //coils
    airInputParamsHeater: "Supply Temperature",
    winterSupplyTemperatureState: "Supply Temperature",
    airInputParamsCooler: "Supply Temperature",
    summerSupplyTemperatureState: "Supply Temperature",
    SummerAirTemperatureIn: 0,
    WinterAirTemperatureIn: 0,
    coilInputTemperatureCooler: 0,
    coilInputTemperatureHeater: 0,
    coilInputHumidityCooler: 0,
    coilInputHumidityHeater: 0,
    heaterAirInputParam: "Supply Temperature",
    heaterAirInputParamValue: 22,
    heaterWaterInputParam: "Temperature out",
    heaterWaterInputParamValue: 28,
    coolerAirInputParam: "Supply Temperature",
    coolerAirInputParamValue: 22,
    coolerWaterInputParam: "Temperature out",
    coolerWaterInputParamValue: 12,
  };

  const [step, setStep] = useState(1);

  const [projectItem, dispatch] = useReducer(mainFormReducer, initialState);
  const [unitTypes, setUnitTypes] = useState([]);
  const isFirstRender = useRef(true);

  // Debug: Log current damper values
  console.log(
    "Current damper values - Height:",
    projectItem.damperHeight,
    "Width:",
    projectItem.damperWidth
  );

  const [initialValues, setInitialValues] = useState({});
  const [selectionExplanation, setSelectionExplanation] = useState("");

  const airFlowCalculatorFields = (area, height, buldingType, changeRate) => {
    dispatch({
      type: "updateBuildingType",
      payload: { name: "buildingType", value: buldingType },
    });
    dispatch({
      type: "updateFlowByArea",
      payload: { name: "area", value: area },
    });
    dispatch({
      type: "updateFlowByHeight",
      payload: { name: "height", value: height },
    });
    dispatch({
      type: "updateFlowByChangeRate",
      payload: { name: "changeRate", value: changeRate },
    });
  };

  const airFlowDirectInputFields = (
    directAirflowSupply,
    directAirflowReturn
  ) => {
    dispatch({
      type: "updateBuildingType",
      payload: { name: "buildingType", value: "None" },
    });
    dispatch({
      type: "updateFlowByArea",
      payload: { name: "area", value: 0 },
    });
    dispatch({
      type: "updateFlowByHeight",
      payload: { name: "height", value: 0 },
    });
    dispatch({
      type: "updateFlowByChangeRate",
      payload: { name: "changeRate", value: 0 },
    });
    dispatch({
      type: "updateProject",
      payload: { name: "flowSupply", value: directAirflowSupply },
    });
    dispatch({
      type: "updateProject",
      payload: { name: "flowReturn", value: directAirflowReturn },
    });
  };

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;

      return; // Skip the first render
    } else if (
      projectItem.summerOutdoorTemperature ===
        initialValues.summerOutdoorTemperature &&
      projectItem.summerWetBulbTemperature ===
        initialValues.summerWetBulbTemperature &&
      projectItem.winterOutdoorTemperature ===
        initialValues.winterOutdoorTemperature
    )
      snackbarDispatch(
        setSnackbar(true, "info", t("snackbar.calculationError"))
      );
  }, [
    projectItem.summerOutdoorTemperature,
    projectItem.summerWetBulbTemperature,
    projectItem.winterOutdoorTemperature,
  ]);

  const calculateAirFlow = () => {
    setUnitTypes([]);
    getAirFlowCalculation(projectItem.flowSupply, projectItem.flowReturn).then(
      (res) => {
        if (res.data.data.length < 1) {
          snackbarDispatch(
            setSnackbar(
              true,
              "error",
              t("projectinformation.flowValError"),
              "",
              {},
              null
            )
          );
          handleValidationError(1);
        } else setUnitTypes(res.data.data);
      }
    );
  };

  function findObjectsWithFieldValue(objectsArray, fieldName, targetValue) {
    // Initialize an array to store matching objects
    let matchingObjects = [];

    // Iterate through each object in the array
    for (let i = 0; i < objectsArray.length; i++) {
      // Check if the object has the specified field
      if (fieldName in objectsArray[i]) {
        // Compare the field value with the target value
        if (objectsArray[i][fieldName] === targetValue) {
          // If they match, add the object to the result array
          matchingObjects.push(objectsArray[i]);
        }
      }
    }
    // Return the array of matching objects
    return matchingObjects.length > 0;
  }
  function validateInput(name, value, minValue, maxValue) {
    let newValue;
    if (value !== "" && value !== "-") {
      if (parseFloat(value) > minValue && parseFloat(value) < maxValue)
        newValue = value;
      else {
        newValue = initialValues[name];
        dispatch(
          setSnackbar(
            true,
            "error",
            t(
              "createproject.validation" + name[0].toUpperCase() + name.slice(1)
            )
          )
        );
      }
    } else newValue = value;
    return newValue;
  }
  useEffect(() => {
    //kalkulacija polja za wet bulb i dew point na osnovu dry bulb i vlaznosti
    dispatch({
      type: "updateSummerConditions",
    });
  }, [projectItem.summerOutdoorTemperature, projectItem.summerOutdoorHumidity]);
  useEffect(() => {
    //kalkulacija polja za wet bulb i dew point na osnovu dry bulb i vlaznosti
    dispatch({
      type: "updateWinterConditions",
    });
  }, [projectItem.summerOutdoorTemperature, projectItem.summerOutdoorHumidity]);

  const handleProjectChange = (e) => {
    let { name, value } = e.target;

    if (e.target.type === "checkbox" || e.target.type === "switch") {
      value = e.target.checked;
    }

    if (name === "buildingType") {
      dispatch({ type: "updateBuildingType", payload: { name, value } });
    } else if (
      name === "summerOutdoorTemperature" ||
      name === "winterOutdoorTemperature"
    ) {
      let newValue = validateInput(name, value, -50, 60);

      dispatch({
        type: "updateProject",
        payload: { name, value: newValue },
      });
    } else if (
      name === "summerOutdoorHumidity" ||
      name === "winterOutdoorHumidity" ||
      name === "summerIndoorHumidity" ||
      name === "winterIndoorHumidity"
    ) {
      let newValue = validateInput(name, value, -1, 101);
      dispatch({
        type: "updateProject",
        payload: { name, value: newValue },
      });
    } else if (name === "area") {
      dispatch({ type: "updateFlowByArea", payload: { name, value } });
    } else if (name === "height") {
      dispatch({ type: "updateFlowByHeight", payload: { name, value } });
    } else if (name === "changeRate") {
      dispatch({ type: "updateFlowByChangeRate", payload: { name, value } });
    } else if (name === "heatRecovery") {
      // Check if this is a locked heat recovery value (Energy:Prime or Aqua:Prime)
      const isLockedHeatRecovery =
        value === "Polypropylene with indirect adiabatic cooling" ||
        value === "Polypropylene";

      if (isLockedHeatRecovery) {
        // For locked states, just update the value without changing sizeOfUnit
        dispatch({
          type: "updateProject",
          payload: { name, value },
        });
      } else {
        // For selectable heat recovery, update sizeOfUnit based on selection
        dispatch({
          type: "updateInitialSizeOfUnitHeatRecovery",
          payload: { name, value, unitTypes },
        });
      }
    } else if (name === "heatRecoveryPlateType") {
      dispatch({
        type: "updateInitialSizeOfUnitHeatRecoveryPlate",
        payload: { name, value, unitTypes },
      });
    } else if (
      name === "fineFilterClassSupply" ||
      name === "filteringAdvancedSettings"
    ) {
      dispatch({
        type: "sameFilterClasses",
        payload: { name, value },
      });
    } /*else if (name === "sizeOfUnit") {
      let details = unitTypes.filter((item) => item.id === value)[0];

      dispatch({
        type: "updateUnitDetails",
        payload: { name, value, details },
      });
    }*/ else {
      dispatch({ type: "updateProject", payload: { name, value } });
    }
  };

  useEffect(() => {
    console.log(
      "useEffect triggered - sizeOfUnit:",
      projectItem.sizeOfUnit,
      "unitTypes length:",
      unitTypes?.length
    );
    setSelectionExplanation("");
    let details = unitTypes?.filter(
      (item) => item.id === projectItem.sizeOfUnit
    )[0];
    console.log("Found details:", details);
    if (details != undefined) {
      dispatch({
        type: "updateUnitDetails",
        payload: { details },
      });

      // Fetch damper data from the new Dampers table
      console.log(
        "Fetching damper data for unitTypeId:",
        projectItem.sizeOfUnit
      );
      getDamperData(projectItem.sizeOfUnit)
        .then((res) => {
          console.log("Damper API response:", res);
          if (res.statusCode >= 200 && res.statusCode < 300) {
            console.log("Using damper data from API:", res.data);
            dispatch({
              type: "updateDamperDetails",
              payload: {
                damperHeight: res.data.damperHeight,
                damperWidth: res.data.damperWidth,
              },
            });
          } else {
            console.log("API returned error status, using fallback values");
            dispatch({
              type: "updateDamperDetails",
              payload: {
                damperHeight: "211",
                damperWidth: "370",
              },
            });
          }
        })
        .catch((err) => {
          console.log("Error fetching damper data:", err);
          console.log(
            "Using fallback values: damperHeight=211, damperWidth=370"
          );
          // Set default values if API call fails
          dispatch({
            type: "updateDamperDetails",
            payload: {
              damperHeight: "211",
              damperWidth: "370",
            },
          });
        });

      setSelectionExplanation(
        "Difference between supply flow and nominal flow is " +
          Math.abs(details.nominalFlow - projectItem.flowSupply) +
          "m³/h. Difference between return flow and nominal flow is " +
          Math.abs(details.nominalFlow - projectItem.flowReturn) +
          "m³/h."
      );
    } else {
      console.log("No details found for sizeOfUnit:", projectItem.sizeOfUnit);
    }
  }, [projectItem.sizeOfUnit, unitTypes]);

  const [active1, setActive1] = useState(false);
  const [active2, setActive2] = useState(false);
  const [active3, setActive3] = useState(false);
  const [active4, setActive4] = useState(false);
  const [active5, setActive5] = useState(false);
  const [active6, setActive6] = useState(false);
  const [active7, setActive7] = useState(false);
  const [active8, setActive8] = useState(false);

  const [checked1, setChecked1] = useState(false);
  const [checked2, setChecked2] = useState(false);
  const [checked3, setChecked3] = useState(false);
  const [checked4, setChecked4] = useState(false);
  const [checked5, setChecked5] = useState(false);
  const [checked6, setChecked6] = useState(false);
  const [checked7, setChecked7] = useState(false);
  const [checked8, setChecked8] = useState(false);

  const areMandatoryFieldsFilled = (proj) => {
    if (
      // Project information
      proj.projName !== "" &&
      proj.area !== "" &&
      proj.height !== "" &&
      proj.buildingType !== "" &&
      proj.changeRate !== "" &&
      proj.dropSupply !== "" &&
      proj.flowSupply !== "" &&
      proj.flowReturn !== "" &&
      proj.dropReturn !== "" &&
      // Type of unit (includes moved Requirements fields)
      proj.ecoDesign !== "" &&
      proj.casingQuality !== "" &&
      proj.installation !== "" &&
      proj.unitType !== "" &&
      proj.heatRecovery !== "" &&
      proj.controls !== "" &&
      proj.unitDivision !== "" &&
      // Operating point
      proj.summerOutdoorTemperature !== "" &&
      proj.summerWetBulbTemperature !== "" &&
      proj.summerDewPointTemperature !== "" &&
      proj.summerOutdoorHumidity !== "" &&
      proj.summerIndoorTemperature !== "" &&
      proj.summerIndoorHumidity !== "" &&
      proj.winterIndoorTemperature !== "" &&
      proj.winterIndoorHumidity !== "" &&
      proj.winterOutdoorTemperature !== "" &&
      proj.winterOutdoorHumidity !== "" &&
      // Filtering
      proj.doubleStageFiltering !== "" &&
      proj.filteringAdvancedSettings !== null &&
      proj.fineFilterType !== "" &&
      proj.fineFilterClassSupply !== "" &&
      // Fan
      proj.fanAdvancedSettings !== "" &&
      proj.fanType !== "" &&
      proj.airflowDirection !== 0
    )
      return true;
    else {
      snackbarDispatch(
        setSnackbar(true, "error", t("snackbar.mandatoryFields"))
      );

      return false;
    }
  };

  {
    /*useEffect(() => {
    console.log(step);
    if (step === 5) {
      setCoilsValSuccess(false);
      const reqData = {
        Guid: projectItem.guid,
        Id: projectItem.projId,
        RevisionNumber: projectItem.revisionNumber,
        SummerWetBulbTemperature: projectItem.summerWetBulbTemperature,

        SummerIndoorTemperature: projectItem.summerIndoorTemperature,
        SummerIndoorHumidity: projectItem.summerIndoorHumidity,
        SummerSupplyTemperature: projectItem.summerSupplyTemperature,
        CoolerFluidType: projectItem.coolerType,
        CoolerFluidConcentration: projectItem.coolerConcentration,
        CoolerFluidTemperatureIn: projectItem.coolerTemperatureIn,
        CoolerFluidTemperatureOut: projectItem.coolerTemperatureOut,

        WinterIndoorTemperature: projectItem.winterIndoorTemperature,
        WinterIndoorHumidity: projectItem.winterIndoorHumidity,
        WinterSupplyTemperature: projectItem.winterSupplyTemperature,
        HeaterFluidType: projectItem.heaterType,
        HeaterFluidConcentration: projectItem.heaterConcentration,
        HeaterFluidTemperatureIn: projectItem.heaterTemperatureIn,
        HeaterFluidTemperatureOut: projectItem.heaterTemperatureOut,
        FlowSupply: projectItem.flowSupply,
        FlowReturn: projectItem.flowReturn,
        HeatRecoveryType: getRecVal(
          projectItem.heatRecovery,
          params?.unitType ? parseInt(params.unitType, 10) : null
        ) === "PPL_4" ||
        getRecVal(
          projectItem.heatRecovery,
          params?.unitType ? parseInt(params.unitType, 10) : null
        ) === "without"
          ? getRecVal(
              projectItem.heatRecovery,
              params?.unitType ? parseInt(params.unitType, 10) : null
            )
          : projectItem.heatRecoveryPlateType,
        recVal: getRecVal(
          projectItem.heatRecovery,
          params?.unitType ? parseInt(params.unitType, 10) : null
        ),
        HeatRecoverySize: projectItem.heatRecoverySize,
        Coils: projectItem.coils,
        Area: projectItem.area,
        ChangeRate: projectItem.changeRate,
        DropReturn: projectItem.dropReturn,
        DropSupply: projectItem.dropSupply,
        Height: projectItem.height,
        SummerDewPointTemperature: projectItem.summerDewPointTemperature,
        SummerDryBulbTemperature: projectItem.summerOutdoorTemperature,
        InternalWidth: projectItem.internalWidth,
        InternalHeight: projectItem.internalHeight,
        FanInstallationWidth: projectItem.fanInstallationWidth,
        FanInstallationHeight: projectItem.fanInstallationHeight,
        Elevation: projectItem.elevation,
        FineFilterClassSupply: projectItem.fineFilterClassSupply,
        FineFilterClassReturn: projectItem.fineFilterClassReturn,
        UnitFilter: projectItem.unitFilter,
        FanType:
          projectItem.fanType === "Standard"
            ? projectItem.fan.split(";")[1]
            : projectItem.fan.split(";")[0],
        ExternalWidth: projectItem.externalWidth,
        ExternalHeight: projectItem.externalHeight,
        DamperWidth: projectItem.damperWidth,
        DamperHeight: projectItem.damperHeight,
        FanMode: projectItem.fanType,
        CoilsWidth: projectItem.coilsWidth,
        CoilsHeight: projectItem.coilsHeight,
      };

      // validateRecAsync(reqData)
      //   .then((res) => {
      //     if (res.statusCode === 200) {
      //       setCoilsValSuccess(true);
      //     } else {
      //       snackbarDispatch(
      //         setSnackbar(
      //           true,
      //           "error",
      //           t("unitType.coilsValError") + "\n",
      //           "",
      //           {},
      //           null
      //         )
      //       );
      //       setCoilsValSuccess(false);
      //       handleValidationError(4);
      //     }
      //   })
      //   .catch((err) => {
      //     console.log(err);
      //     snackbarDispatch(
      //       setSnackbar(
      //         true,
      //         "error",
      //         t("unitType.coilsValError") + "\n" + err,
      //         "",
      //         {},
      //         null
      //       )
      //     );
      //     setCoilsValSuccess(false);
      //     handleValidationError(4);
      //   });
    } else if (step === 6) {
      setCoilsValSuccess(false);

      const reqData = {
        Guid: projectItem.guid,
        Id: projectItem.projId,
        RevisionNumber: projectItem.revisionNumber,

        SummerRelativeHumidity: projectItem.summerOutdoorHumidity,
        SummerIndoorTemperature: projectItem.summerIndoorTemperature,
        SummerIndoorHumidity: projectItem.summerIndoorHumidity,
        SummerSupplyTemperature:
          projectItem.airInputParamsCooler === "Capacity"
            ? -99
            : projectItem.summerSupplyTemperature,
        // SummerAirTemperatureIn: projectItem.SummerAirTemperatureIn,
        CoolerFluidType: projectItem.coolerType,
        CoolerFluidConcentration: projectItem.coolerConcentration,
        CoolerFluidTemperatureIn: projectItem.coolerTemperatureIn,
        CoolerFluidTemperatureOut: projectItem.coolerTemperatureOut,
        WinterOutdoorTemperature: projectItem.winterOutdoorTemperature,
        WinterOutdoorHumidity: projectItem.winterOutdoorHumidity,
        WinterIndoorTemperature: projectItem.winterIndoorTemperature,
        WinterIndoorHumidity: projectItem.winterIndoorHumidity,
        WinterSupplyTemperature:
          projectItem.airInputParamsHeater === "Capacity"
            ? -99
            : projectItem.winterSupplyTemperature,
        WinterAirTemperatureIn: projectItem.WinterAirTemperatureIn,
        HeaterFluidType: projectItem.heaterType,
        HeaterFluidConcentration: projectItem.heaterConcentration,
        HeaterFluidTemperatureIn: projectItem.heaterTemperatureIn,
        HeaterFluidTemperatureOut: projectItem.heaterTemperatureOut,
        CoilsWidth: projectItem.coilsWidth,
        CoilsHeight: projectItem.coilsHeight,
        Coils: projectItem.coils,
        CapacityInCooler:
          projectItem.airInputParamsCooler === "Capacity"
            ? projectItem.summerSupplyTemperature
            : 0,
        CapacityInHeater:
          projectItem.airInputParamsHeater === "Capacity"
            ? projectItem.winterSupplyTemperature
            : 0,
      };

      validateCoilAsync(reqData).then((res) => {
        if (res.statusCode === 200) {
          setCoilsValSuccess(true);
        } else {
          snackbarDispatch(
            setSnackbar(
              true,
              "error",
              t("unitType.coilsValError") + "\n",
              "",
              {},
              null
            )
          );
          setCoilsValSuccess(false);
          handleValidationError(4);
        }
      });
    }
  }, [step]);

  const checkFanValidity = () => {
    const reqData = {
      Guid: projectItem.guid,
      FanType:
        projectItem.fanType === "Standard"
          ? projectItem.fan.split(";")[1]
          : projectItem.fan.split(";")[0],
      FanMode: projectItem.fanType,
      DesignDryBulbTemperature: projectItem.designDryBulbTemperature,
      DesignDewPointTemperature: projectItem.designDewPointTemperature,
      DesignWetBulbTemperature: projectItem.designWetBulbTemperature,
      FineFilterClassSupply: projectItem.fineFilterClassSupply,
      FineFilterClassReturn: projectItem.fineFilterClassReturn,
      UnitFilter: projectItem.unitFilter,
    };
    validateFanAsync(reqData)
      .then((res) => {
        if (res.statusCode === 200) {
          setFanValSuccess(true);
        } else if (res.statusCode === 404) {
          snackbarDispatch(
            setSnackbar(
              true,
              "error",
              t("fan.fanValError") + "\n",
              "",
              {},
              null
            )
          );
          setFanValSuccess(false);
          handleValidationError(6);
        }
      })
      .catch((err) => {
        snackbarDispatch(
          setSnackbar(
            true,
            "error",
            t("fan.fanValError") + "\n" + err,
            "",
            {},
            null
          )
        );
        setFanValSuccess(false);
        handleValidationError(6);
      });
  };
  }

  useEffect(() => {
    if (step === 7 && coilsValSuccess === true) checkFanValidity();
  }, [step, coilsValSuccess]);*/
  }

  function areMandatoryStepFieldsFilled(proj, step) {
    // async

    switch (step) {
      case 1:
        if (
          // Project information
          proj.projName !== "" &&
          proj.area !== "" &&
          proj.height !== "" &&
          proj.buildingType !== "" &&
          proj.changeRate !== "" &&
          proj.dropSupply !== "" &&
          proj.flowSupply !== "" &&
          proj.flowReturn !== "" &&
          proj.dropReturn !== ""
        ) {
          if (
            proj.flowSupply < 600 ||
            proj.flowSupply > 6800 ||
            proj.flowReturn < 600 ||
            proj.flowReturn > 6800
          ) {
            snackbarDispatch(
              setSnackbar(true, "error", t("snackbar.invalidFlow"))
            );
            return false;
          } else return true;
        } else {
          snackbarDispatch(
            setSnackbar(true, "error", t("snackbar.mandatoryFields"))
          );

          return false;
        }

      case 2:
        const baseUnitTypeStep2 = params?.unitType
          ? parseInt(params.unitType, 10)
          : null;
        // For baseUnitTypeStep2 2 (Energy:Prime) and 4 (Aqua:Prime), unitType subtype is required
        // For baseUnitTypeStep2 1 (Compact:Prime), unitType is set from route param, so no validation needed
        const unitTypeValid =
          baseUnitTypeStep2 === 1 ||
          (baseUnitTypeStep2 !== 1 && proj.unitType != "");

        // Check if coils is required
        // For locked states (Energy:Prime, Aqua:Prime), values are set automatically via useEffect
        // For Compact:Prime, user must select
        const coilsValid = proj.coils != "";

        // Check if heatRecovery is required
        // For locked states (Energy:Prime, Aqua:Prime), values are set automatically via useEffect
        // For Compact:Prime, user must select
        const heatRecoveryValid = proj.heatRecovery != "";

        // Debug logging for validation
        if (step === 2) {
          console.log("=== VALIDATION CHECK - STEP 2 ===");
          console.log("Base Unit Type:", baseUnitTypeStep2);
          console.log("Unit Type:", proj.unitType);
          console.log("Unit Type Valid:", unitTypeValid);
          console.log("--- Field Values ---");
          console.log(
            "installation:",
            proj.installation,
            proj.installation != "" ? "✓" : "✗ MISSING"
          );
          console.log(
            "unitType:",
            proj.unitType,
            unitTypeValid ? "✓" : "✗ MISSING"
          );
          console.log("coils:", proj.coils, coilsValid ? "✓" : "✗ MISSING");
          console.log(
            "heatRecovery:",
            proj.heatRecovery,
            heatRecoveryValid ? "✓" : "✗ MISSING"
          );
          console.log(
            "unitDivision:",
            proj.unitDivision,
            proj.unitDivision != "" ? "✓" : "✗ MISSING"
          );
          console.log(
            "airflowDirection:",
            proj.airflowDirection,
            proj.airflowDirection !== 0 ? "✓" : "✗ MISSING"
          );
          console.log(
            "ecoDesign:",
            proj.ecoDesign,
            proj.ecoDesign != "" ? "✓" : "✗ MISSING"
          );
          console.log(
            "casingQuality:",
            proj.casingQuality,
            proj.casingQuality != "" ? "✓" : "✗ MISSING"
          );
          console.log("sizeOfUnit:", proj.sizeOfUnit);
          console.log(
            "sizeOfUnit valid:",
            findObjectsWithFieldValue(unitTypes, "id", proj.sizeOfUnit)
              ? "✓"
              : "✗ MISSING"
          );
          console.log("unitTypes array length:", unitTypes?.length || 0);
          console.log("================================");
        }

        // Check each field individually and log which ones are missing
        const validationChecks = {
          installation: proj.installation != "",
          unitType: unitTypeValid,
          coils: coilsValid,
          heatRecovery: heatRecoveryValid,
          unitDivision: proj.unitDivision != "",
          airflowDirection: proj.airflowDirection !== 0,
          ecoDesign: proj.ecoDesign != "",
          casingQuality: proj.casingQuality != "",
          sizeOfUnit: findObjectsWithFieldValue(
            unitTypes,
            "id",
            proj.sizeOfUnit
          ),
        };

        const missingFields = Object.entries(validationChecks)
          .filter(([field, isValid]) => !isValid)
          .map(([field]) => field);

        if (missingFields.length > 0 && step === 2) {
          console.error(
            "❌ VALIDATION FAILED - Missing fields:",
            missingFields
          );
          console.error("Field details:", {
            installation: {
              value: proj.installation,
              valid: validationChecks.installation,
            },
            unitType: {
              value: proj.unitType,
              valid: validationChecks.unitType,
              baseUnitType: baseUnitTypeStep2,
            },
            coils: { value: proj.coils, valid: validationChecks.coils },
            heatRecovery: {
              value: proj.heatRecovery,
              valid: validationChecks.heatRecovery,
            },
            unitDivision: {
              value: proj.unitDivision,
              valid: validationChecks.unitDivision,
            },
            airflowDirection: {
              value: proj.airflowDirection,
              valid: validationChecks.airflowDirection,
            },
            ecoDesign: {
              value: proj.ecoDesign,
              valid: validationChecks.ecoDesign,
            },
            casingQuality: {
              value: proj.casingQuality,
              valid: validationChecks.casingQuality,
            },
            sizeOfUnit: {
              value: proj.sizeOfUnit,
              valid: validationChecks.sizeOfUnit,
              unitTypesLength: unitTypes?.length,
            },
          });
        }

        if (
          validationChecks.installation &&
          validationChecks.unitType &&
          validationChecks.coils &&
          validationChecks.heatRecovery &&
          validationChecks.unitDivision &&
          validationChecks.airflowDirection &&
          validationChecks.ecoDesign &&
          validationChecks.casingQuality &&
          validationChecks.sizeOfUnit
        ) {
          if (step === 2) {
            console.log("✅ VALIDATION PASSED - All fields are valid");
          }
          return true;
        } else {
          snackbarDispatch(
            setSnackbar(true, "error", t("snackbar.mandatoryFields"))
          );
          setSelectionExplanation(" ");

          return false;
        }
      case 3:
        /*try {
          setLoading(true);
          const data = await validateProjectItemRec({
            Guid: projectItem.guid,
            Id: projectItem.projId,
            RevisionNumber: projectItem.revisionNumber,
            SummerWetBulbTemperature: projectItem.summerWetBulbTemperature,
            SummerRelativeHumidity: projectItem.summerOutdoorHumidity,
            SummerIndoorTemperature: projectItem.summerIndoorTemperature,
            SummerIndoorHumidity: projectItem.summerIndoorHumidity,
            SummerSupplyTemperature: projectItem.summerSupplyTemperature,
            CoolerFluidType: projectItem.coolerType,
            CoolerFluidConcentration: projectItem.coolerConcentration,
            CoolerFluidTemperatureIn: projectItem.coolerTemperatureIn,
            CoolerFluidTemperatureOut: projectItem.coolerTemperatureOut,
            WinterOutdoorTemperature: projectItem.winterOutdoorTemperature,
            WinterOutdoorHumidity: projectItem.winterOutdoorHumidity,
            WinterIndoorTemperature: projectItem.winterIndoorTemperature,
            WinterIndoorHumidity: projectItem.winterIndoorHumidity,
            WinterSupplyTemperature: projectItem.winterSupplyTemperature,
            HeaterFluidType: projectItem.heaterType,
            HeaterFluidConcentration: projectItem.heaterConcentration,
            HeaterFluidTemperatureIn: projectItem.heaterTemperatureIn,
            HeaterFluidTemperatureOut: projectItem.heaterTemperatureOut,
            FlowSupply: projectItem.flowSupply,
            FlowReturn: projectItem.flowReturn,
            HeatRecoveryType: getRecVal(
          projectItem.heatRecovery,
          params?.unitType ? parseInt(params.unitType, 10) : null
        ) === "PPL_4" ||
        getRecVal(
          projectItem.heatRecovery,
          params?.unitType ? parseInt(params.unitType, 10) : null
        ) === "without"
          ? getRecVal(
              projectItem.heatRecovery,
              params?.unitType ? parseInt(params.unitType, 10) : null
            )
          : projectItem.heatRecoveryPlateType,
        recVal: getRecVal(
          projectItem.heatRecovery,
          params?.unitType ? parseInt(params.unitType, 10) : null
        ),
            HeatRecoverySize: projectItem.heatRecoverySize,
            Coils: projectItem.coils,
            Area: projectItem.area,
            ChangeRate: projectItem.changeRate,
            DropReturn: projectItem.dropReturn,
            DropSupply: projectItem.dropSupply,
            Height: projectItem.height,
            SummerDewPointTemperature: projectItem.summerDewPointTemperature,
            SummerDryBulbTemperature: projectItem.summerOutdoorTemperature,
            InternalWidth: projectItem.internalWidth,
            InternalHeight: projectItem.internalHeight,
            FanInstallationWidth: projectItem.fanInstallationWidth,
            FanInstallationHeight: projectItem.fanInstallationHeight,
            Elevation: projectItem.elevation,
            FineFilterClassSupply: projectItem.fineFilterClassSupply,
            FineFilterClassReturn: projectItem.fineFilterClassReturn,
            UnitFilter: projectItem.unitFilter,
            FanType: projectItem.fan.split(";")[0],
            ExternalWidth: projectItem.externalWidth,
            ExternalHeight: projectItem.externalHeight,
            DamperWidth: projectItem.damperWidth,
            DamperHeight: projectItem.damperHeight,
            FanMode: projectItem.fanType,
          });*/
        if (
          proj.heatRecovery === "Plate heat exchanger" &&
          proj.heatRecoveryPlateType === "Condensing" &&
          (proj.summerOutdoorTemperature < proj.summerIndoorTemperature ||
            proj.winterOutdoorTemperature > proj.winterIndoorTemperature)
        ) {
          snackbarDispatch(
            setSnackbar(true, "error", t("snackbar.temperatureError"))
          );
          return false;
        } else if (
          proj.summerOutdoorTemperature !== "" &&
          proj.summerWetBulbTemperature !== "" &&
          proj.summerDewPointTemperature !== "" &&
          proj.summerOutdoorHumidity !== "" &&
          proj.summerIndoorTemperature !== "" &&
          proj.summerIndoorHumidity !== "" &&
          proj.winterIndoorTemperature !== "" &&
          proj.winterIndoorHumidity !== "" &&
          proj.winterOutdoorTemperature !== "" &&
          proj.winterOutdoorHumidity !== ""
        )
          return true;
        else {
          snackbarDispatch(
            setSnackbar(true, "error", t("snackbar.mandatoryFields"))
          );
          return false;
        }

      /* } catch (error) {
          snackbarDispatch(
            setSnackbar(
              true,
              "error",
              t("unitType.coilsValError") + "\n" + error,
              "",
              {},
              null
            )
          );
          return false;
        } finally {
          setLoading(false);
        }*/

      // return data;

      case 4:
        // Heat Recovery validation - always passes (read-only display)
        return true;

      case 5:
        // Coils validation - always passes (handled in step 5 logic)
        return true;

      case 6:
        if (
          proj.doubleStageFiltering != "" &&
          proj.filteringAdvancedSettings != null &&
          proj.fineFilterType != "" &&
          proj.fineFilterClassSupply != ""
        )
          return true;
        else {
          snackbarDispatch(
            setSnackbar(true, "error", t("snackbar.mandatoryFields"))
          );

          return false;
        }

      case 7:

      default:
        return areMandatoryFieldsFilled(proj);
    }
  }

  function findClosestKeyLessThan(object, targetValue) {
    // Get an array of object values that are less than the target value
    const filteredValues = Object.values(object).filter(
      (value) => value < targetValue
    );

    // If there are no values less than the target, return null or handle as needed
    if (filteredValues.length === 0) {
      return null;
    }

    // Find the maximum value among the filtered values
    const closestValue = Math.max(...filteredValues);

    // Get the corresponding key using the value
    const closestKey = Object.keys(object).find(
      (key) => object[key] === closestValue
    );

    return closestKey;
  }

  useEffect(() => {
    let ethyleneConcentration = findClosestKeyLessThan(
      etyleneGlycol,
      projectItem.winterOutdoorTemperature
    );
    let propyleneConcentration = findClosestKeyLessThan(
      propyleneGlycol,
      projectItem.winterOutdoorTemperature
    );
    switch (projectItem.heaterType) {
      case "ethylene":
        dispatch({
          type: "updateProject",
          payload: {
            name: "heaterConcentration",
            value: ethyleneConcentration,
          },
        });
        break;
      case "propylene":
        dispatch({
          type: "updateProject",
          payload: {
            name: "heaterConcentration",
            value: propyleneConcentration,
          },
        });
        break;
      case "water":
        dispatch({
          type: "updateProject",
          payload: { name: "heaterConcentration", value: 0 },
        });
    }
    switch (projectItem.coolerType) {
      case "ethylene":
        dispatch({
          type: "updateProject",
          payload: {
            name: "coolerConcentration",
            value: ethyleneConcentration,
          },
        });
        break;
      case "propylene":
        dispatch({
          type: "updateProject",
          payload: {
            name: "coolerConcentration",
            value: propyleneConcentration,
          },
        });
        break;
      case "water":
        dispatch({
          type: "updateProject",
          payload: { name: "coolerConcentration", value: 0 },
        });
    }
  }, [
    projectItem.winterOutdoorTemperature,
    projectItem.heaterType,
    projectItem.coolerType,
  ]);

  useEffect(() => {
    if (airTempInHeat == false) {
      dispatch({
        type: "updateProject",
        payload: {
          name: "coilInputTemperatureHeater",
          value: projectItem.winterRecTemperatureOut,
        },
      });
      dispatch({
        type: "updateProject",
        payload: {
          name: "coilInputHumidityHeater",
          value: projectItem.winterRecHumidityOut,
        },
      });
    }
    if (airTempInCool == false) {
      dispatch({
        type: "updateProject",
        payload: {
          name: "coilInputTemperatureCooler",
          value: projectItem.summerRecTemperatureOut,
        },
      });
      dispatch({
        type: "updateProject",
        payload: {
          name: "coilInputHumidityCooler",
          value: projectItem.summerRecHumidityOut,
        },
      });
    }
  }, [airTempInHeat, airTempInCool]);

  const onSubmit = (e) => {
    setLoading(true);
    const validacija = areMandatoryFieldsFilled(projectItem);

    const requestData = {
      ...projectItem,
      localDateTime: new Date(),
      timeZone: new Date().getTimezoneOffset(),
      //Dodata su polja za select u Coils komponenti
      coolerCapacity:
        projectItem.coolerAirInputParam == "Supply Temperature"
          ? 0
          : projectItem.coolerAirInputParamValue,
      coolerFlow:
        projectItem.coolerWaterInputParam == "Temperature out"
          ? 0
          : projectItem.coolerWaterInputParam,
      heaterCapacity:
        projectItem.heaterAirInputParam == "Supply Temperature"
          ? 0
          : projectItem.heaterAirInputParamValue,
      heaterFlow:
        projectItem.heaterWaterInputParam == "Temperature out"
          ? 0
          : projectItem.heaterWaterInputParamValue,
      CoolerTemperatureOut:
        projectItem.coolerWaterInputParam != "Temperature out"
          ? "-99"
          : projectItem.coolerWaterInputParamValue.toString(),
      HeaterTemperatureOut:
        projectItem.heaterWaterInputParam != "Temperature out"
          ? "-99"
          : projectItem.heaterWaterInputParamValue.toString(),
    };

    !isEditing
      ? validacija
        ? createProjectItemBlob(requestData)
            .then((response) => {
              if (response.statusCode >= 200 && response.statusCode < 300) {
                snackbarDispatch(
                  setSnackbar(
                    true,
                    "success",
                    t("snackbar.createProjectItemSuccess"),
                    "/project-item-revision",

                    {
                      replace: true,
                      state: {
                        projectItem: projectItem,
                        id: response.data,
                      },
                    }
                  )
                );

                setLoading(false);
              } else {
                snackbarDispatch(
                  setSnackbar(
                    true,
                    "error",
                    t("snackbar.createProjectItemError")
                  )
                );
                setLoading(false);
              }
            })
            .catch((err) => {
              snackbarDispatch(
                setSnackbar(true, "error", t("snackbar.createProjectItemError"))
              );
              setLoading(false);
            })
        : setLoading(false)
      : editProjectItem(requestData).then((response) => {
          if (response.statusCode >= 200 && response.statusCode < 300) {
            snackbarDispatch(
              setSnackbar(
                true,
                "success",
                t("snackbar.editProjectItemSuccess"),
                "/project-item-revision",

                { replace: true, state: { id: projectItem.id } }
              )
            );
            setLoading(false);
          } else {
            snackbarDispatch(
              setSnackbar(true, "error", t("snackbar.createProjectItemError"))
            );
            setLoading(false);
          }
        });
  };
  //const childRef = useRef(null);
  function handleNext() {
    //  async
    window.scrollTo(0, 0);
    /* if (childRef.current) {
      childRef.current.validate();
    }*/
    let validacija = false;
    try {
      validacija = areMandatoryStepFieldsFilled(projectItem, step); //await

      if (validacija) {
        // Check if we should skip Heat Recovery step (for Compact:Prime)
        const baseUnitType = params?.unitType
          ? parseInt(params.unitType, 10)
          : null;
        const shouldSkipHeatRecovery = baseUnitType === 1; // Compact:Prime

        setStep((prevStep) => {
          // Skip Heat Recovery step for Compact:Prime
          if (step === 2 && shouldSkipHeatRecovery) {
            return prevStep + 2; // Skip step 4 (Heat Recovery), go to step 5 (Coils)
          }
          if (projectItem.coils === "without" && step === 4) {
            // Skip Coils step if coils is "without"
            return prevStep + 2;
          } else if (projectItem.coils === "without" && step === 3) {
            // Old logic for skipping Coils from Operating Point
            return prevStep;
          } else {
            return prevStep + 1;
          }
        });
        if (step === 1) {
          setActive1(true);
          setChecked2(true);
        }
        if (step === 2) {
          setActive2(true);
          setChecked3(true);
        }
        if (step === 3) {
          setActive3(true);
          setChecked4(true);
        }
        if (step === 4) {
          setActive4(true);
          setChecked5(true);
        }
        if (step === 3) {
          (async function () {
            setLoading(true);
            console.log(projectItem);
            try {
              const response = await validateRecAsync({
                Guid: projectItem.guid.toString(),
                Id: projectItem.projId.toString(),
                RevisionNumber: projectItem.revisionNumber.toString(),
                SummerWetBulbTemperature:
                  projectItem.summerWetBulbTemperature.toString(),
                SummerRelativeHumidity:
                  projectItem.summerOutdoorHumidity.toString(),
                SummerIndoorTemperature:
                  projectItem.summerIndoorTemperature.toString(),
                SummerIndoorHumidity:
                  projectItem.summerIndoorHumidity.toString(),

                WinterOutdoorTemperature:
                  projectItem.winterOutdoorTemperature.toString(),
                WinterOutdoorHumidity:
                  projectItem.winterOutdoorHumidity.toString(),
                WinterIndoorTemperature:
                  projectItem.winterIndoorTemperature.toString(),
                WinterIndoorHumidity:
                  projectItem.winterIndoorHumidity.toString(),
                HeaterFluidType: projectItem.heaterType.toString(),
                HeaterFluidConcentration:
                  projectItem.heaterConcentration.toString(),
                FlowSupply: projectItem.flowSupply.toString(),
                FlowReturn: projectItem.flowReturn.toString(),
                HeatRecoveryType:
                  getRecVal(
                    projectItem.heatRecovery,
                    params?.unitType ? parseInt(params.unitType, 10) : null
                  ) === "PPL_4" ||
                  getRecVal(
                    projectItem.heatRecovery,
                    params?.unitType ? parseInt(params.unitType, 10) : null
                  ) === "without"
                    ? getRecVal(
                        projectItem.heatRecovery,
                        params?.unitType ? parseInt(params.unitType, 10) : null
                      )
                    : projectItem.heatRecoveryPlateType.toString(),
                HeatRecoverySize: projectItem.heatRecoverySize.toString(),
                Coils: getCoilVal(
                  projectItem.coils,
                  params?.unitType ? parseInt(params.unitType, 10) : null,
                  projectItem.unitType
                ).toString(),
                recVal: getRecVal(
                  projectItem.heatRecovery,
                  params?.unitType ? parseInt(params.unitType, 10) : null
                ),
                Area: projectItem.area.toString(),
                ChangeRate: projectItem.changeRate.toString(),
                DropReturn: projectItem.dropReturn.toString(),
                DropSupply: projectItem.dropSupply.toString(),
                Height: projectItem.height.toString(),
                SummerDewPointTemperature:
                  projectItem.summerDewPointTemperature.toString(),
                SummerDryBulbTemperature:
                  projectItem.summerOutdoorTemperature.toString(),
                InternalWidth: projectItem.internalWidth.toString(),
                InternalHeight: projectItem.internalHeight.toString(),
                FanInstallationWidth:
                  projectItem.fanInstallationWidth.toString(),
                FanInstallationHeight:
                  projectItem.fanInstallationHeight.toString(),
                Elevation: projectItem.elevation.toString(),
                FineFilterClassSupply:
                  projectItem.fineFilterClassSupply.toString(),
                FineFilterClassReturn:
                  projectItem.fineFilterClassReturn.toString(),
                UnitFilter: projectItem.unitFilter.toString(),
                FanType:
                  projectItem.fanType === "Standard"
                    ? projectItem.fan.split(";")[1].toString()
                    : projectItem.fan.split(";")[0].toString(),
                ExternalWidth: projectItem.externalWidth.toString(),
                ExternalHeight: projectItem.externalHeight.toString(),
                DamperWidth: projectItem.damperWidth.toString(),
                DamperHeight: projectItem.damperHeight.toString(),
                FanMode: projectItem.fanType.toString(),
                CoilsWidth: projectItem.coilsWidth.toString(),
                CoilsHeight: projectItem.coilsHeight.toString(),
                UnitTypeId: projectItem.sizeOfUnit.toString(),
                AirflowDirection: parseInt(projectItem.airflowDirection),
              });
              dispatch({
                type: "updateRecData",
                payload: {
                  coilInputTemperatureCooler:
                    response.data.summerRecCalculation.sup_AirTempOut,
                  coilInputTemperatureHeater:
                    response.data.winterRecCalculation.sup_AirTempOut,
                  coilInputHumidityCooler:
                    response.data.summerRecCalculation.sup_HumidityOut,
                  coilInputHumidityHeater:
                    response.data.winterRecCalculation.sup_HumidityOut,
                  // Store full recVal response for Heat Recovery tab
                  summerRecCalculation: response.data.summerRecCalculation,
                  winterRecCalculation: response.data.winterRecCalculation,
                },
              });

              if (projectItem.coils === "without") {
                (async function () {
                  setLoading(true);

                  try {
                    const response = await validateCoilAsync({
                      Guid: projectItem.guid,
                      SummerRelativeHumidity:
                        projectItem.summerOutdoorHumidity.toString(),
                      SummerIndoorTemperature:
                        projectItem.summerIndoorTemperature.toString(),
                      SummerIndoorHumidity:
                        projectItem.summerIndoorHumidity.toString(),
                      SummerSupplyTemperature:
                        projectItem.coolerAirInputParam === "Capacity"
                          ? "-99"
                          : projectItem.coolerAirInputParamValue.toString(),
                      // SummerAirTemperatureIn: projectItem.SummerAirTemperatureIn,
                      CoolerFluidType: projectItem.coolerType,
                      CoolerFluidConcentration:
                        projectItem.coolerConcentration.toString(),
                      CoolerFluidTemperatureIn:
                        projectItem.coolerTemperatureIn.toString(),
                      CoolerFluidTemperatureOut:
                        projectItem.coolerWaterInputParam != "Temperature out"
                          ? "-99"
                          : projectItem.coolerWaterInputParamValue.toString(),
                      CoolerMediumFlowRate:
                        projectItem.coolerWaterInputParam != "Temperature out"
                          ? projectItem.coolerWaterInputParamValue.toString()
                          : "-99",
                      WinterOutdoorTemperature:
                        projectItem.winterOutdoorTemperature.toString(),
                      WinterOutdoorHumidity:
                        projectItem.winterOutdoorHumidity.toString(),
                      WinterIndoorTemperature:
                        projectItem.winterIndoorTemperature.toString(),
                      WinterIndoorHumidity:
                        projectItem.winterIndoorHumidity.toString(),
                      WinterSupplyTemperature:
                        projectItem.heaterAirInputParam === "Capacity"
                          ? "-99"
                          : projectItem.heaterAirInputParamValue.toString(),

                      HeaterFluidType: projectItem.heaterType,
                      HeaterFluidConcentration:
                        projectItem.heaterConcentration.toString(),
                      HeaterFluidTemperatureIn:
                        projectItem.heaterTemperatureIn.toString(),
                      HeaterFluidTemperatureOut:
                        projectItem.heaterWaterInputParam != "Temperature out"
                          ? "-99"
                          : projectItem.heaterWaterInputParamValue.toString(),

                      HeaterMediumFlowRate:
                        projectItem.heaterWaterInputParam != "Temperature out"
                          ? projectItem.heaterWaterInputParamValue.toString()
                          : "-99",
                      CoilsWidth: projectItem.coilsWidth.toString(),
                      CoilsHeight: projectItem.coilsHeight.toString(),
                      Coils: getCoilVal(
                        projectItem.coils,
                        params?.unitType ? parseInt(params.unitType, 10) : null,
                        projectItem.unitType
                      ),
                      CapacityInCooler:
                        projectItem.coolerAirInputParam === "Capacity"
                          ? projectItem.coolerAirInputParamValue.toString()
                          : "-99",
                      CapacityInHeater:
                        projectItem.heaterAirInputParam === "Capacity"
                          ? projectItem.heaterAirInputParamValue.toString()
                          : "-99",
                      SummerAirTemperatureIn:
                        projectItem.coilInputTemperatureCooler.toString(),
                      SummerAirHumidityIn:
                        projectItem.coilInputHumidityCooler.toString(),
                      WinterAirTemperatureIn:
                        projectItem.coilInputTemperatureHeater.toString(),
                      WinterAirHumidityIn: 0,
                      UnitTypeId: projectItem.sizeOfUnit.toString(),
                    });

                    if (response.statusCode === 200) {
                      setActive5(true);
                      // setChecked6(true);
                      setStep(6); // Coils is now step 6 (was 5)
                    }
                    // Handle the response
                  } catch (error) {
                    // Handle the error
                    setStep((prevStep) => prevStep);
                    snackbarDispatch(setSnackbar(true, "error", error.message));
                  } finally {
                    setLoading(false);
                  }
                })();
              }

              setActive3(true);

              setChecked4(true);

              // Handle the response
            } catch (error) {
              setStep((prevStep) => prevStep - 1);
              snackbarDispatch(setSnackbar(true, "error", error.message));
            } finally {
              setLoading(false);
            }
          })();
        }
        if (step === 5) {
          (async function () {
            setLoading(true);

            try {
              const response = await validateCoilAsync({
                Guid: projectItem.guid,
                SummerRelativeHumidity:
                  projectItem.summerOutdoorHumidity.toString(),
                SummerIndoorTemperature:
                  projectItem.summerIndoorTemperature.toString(),
                SummerIndoorHumidity:
                  projectItem.summerIndoorHumidity.toString(),
                SummerSupplyTemperature:
                  projectItem.coolerAirInputParam === "Capacity"
                    ? "-99"
                    : projectItem.coolerAirInputParamValue.toString(),
                // SummerAirTemperatureIn: projectItem.SummerAirTemperatureIn,
                CoolerFluidType: projectItem.coolerType,
                CoolerFluidConcentration:
                  projectItem.coolerConcentration.toString(),
                CoolerFluidTemperatureIn:
                  projectItem.coolerTemperatureIn.toString(),
                CoolerFluidTemperatureOut:
                  projectItem.coolerWaterInputParam != "Temperature out"
                    ? "-99"
                    : projectItem.coolerWaterInputParamValue.toString(),
                CoolerMediumFlowRate:
                  projectItem.coolerWaterInputParam != "Temperature out"
                    ? projectItem.coolerWaterInputParamValue.toString()
                    : "-99",
                WinterOutdoorTemperature:
                  projectItem.winterOutdoorTemperature.toString(),
                WinterOutdoorHumidity:
                  projectItem.winterOutdoorHumidity.toString(),
                WinterIndoorTemperature:
                  projectItem.winterIndoorTemperature.toString(),
                WinterIndoorHumidity:
                  projectItem.winterIndoorHumidity.toString(),
                WinterSupplyTemperature:
                  projectItem.heaterAirInputParam === "Capacity"
                    ? "-99"
                    : projectItem.heaterAirInputParamValue.toString(),

                HeaterFluidType: projectItem.heaterType,
                HeaterFluidConcentration:
                  projectItem.heaterConcentration.toString(),
                HeaterFluidTemperatureIn:
                  projectItem.heaterTemperatureIn.toString(),
                HeaterFluidTemperatureOut:
                  projectItem.heaterWaterInputParam != "Temperature out"
                    ? "-99"
                    : projectItem.heaterWaterInputParamValue.toString(),

                HeaterMediumFlowRate:
                  projectItem.heaterWaterInputParam != "Temperature out"
                    ? projectItem.heaterWaterInputParamValue.toString()
                    : "-99",
                CoilsWidth: projectItem.coilsWidth.toString(),
                CoilsHeight: projectItem.coilsHeight.toString(),
                Coils: getCoilVal(
                  projectItem.coils,
                  params?.unitType ? parseInt(params.unitType, 10) : null,
                  projectItem.unitType
                ),
                CapacityInCooler:
                  projectItem.coolerAirInputParam === "Capacity"
                    ? projectItem.coolerAirInputParamValue.toString()
                    : "-99",
                CapacityInHeater:
                  projectItem.heaterAirInputParam === "Capacity"
                    ? projectItem.heaterAirInputParamValue.toString()
                    : "-99",
                SummerAirTemperatureIn:
                  projectItem.coilInputTemperatureCooler.toString(),
                SummerAirHumidityIn:
                  projectItem.coilInputHumidityCooler.toString(),
                WinterAirTemperatureIn:
                  projectItem.coilInputTemperatureHeater.toString(),
                WinterAirHumidityIn:
                  projectItem.coilInputHumidityHeater.toString(),
                UnitTypeId: projectItem.sizeOfUnit.toString(),
              });

              if (response.statusCode === 200) {
                setActive5(true);
                setChecked6(true);
              }
              // Handle the response
            } catch (error) {
              // Handle the error
              setStep((prevStep) => prevStep - 1);
              snackbarDispatch(setSnackbar(true, "error", error.message));
            } finally {
              setLoading(false);
            }
          })();
        }
        if (step === 6) {
          setActive6(true);
          setChecked7(true);
        }
        if (step === 7) {
          (async function () {
            setLoading(true);
            try {
              const response = await validateFanAsync({
                Guid: projectItem.guid.toString(),
                FanType:
                  projectItem.fanType === "Standard"
                    ? projectItem.fan.split(";")[1].toString()
                    : projectItem.fan.split(";")[0].toString(),
                FanMode: projectItem.fanType.toString(),
                DesignDryBulbTemperature:
                  projectItem.designDryBulbTemperature.toString(),
                DesignDewPointTemperature:
                  projectItem.designDewPointTemperature.toString(),
                DesignWetBulbTemperature:
                  projectItem.designWetBulbTemperature.toString(),
                DesignWinterOutdoorTemperature:
                  projectItem.designWinterOutdoorTemperature.toString(),
                FineFilterClassSupply:
                  projectItem.fineFilterClassSupply.toString(),
                FineFilterClassReturn:
                  projectItem.fineFilterClassReturn.toString(),
                UnitFilter: projectItem.unitFilter.toString(),
                UnitTypeId: projectItem.sizeOfUnit.toString(),
                FanInstallationWidth:
                  projectItem.fanInstallationWidth.toString(),
                FanInstallationHeight:
                  projectItem.fanInstallationHeight.toString(),
                Insulation: projectItem.casingQuality.toString(),
              });

              setActive7(true);
              // setChecked8(true);
              // Handle the response
            } catch (error) {
              setStep((prevStep) => prevStep - 1);
              snackbarDispatch(setSnackbar(true, "error", error.message));
            } finally {
              setLoading(false);
            }
          })();
        }
        let items = document.querySelectorAll(".sidebar_item");
        for (let i = 0; i < items.length; i++) {
          const element = items[i];
          element.classList.remove("active");
          element.style.border = "none";
        }

        // Use shouldSkipHeatRecovery from function scope (already declared at top of handleNext)
        if (step == 5 && projectItem.coils === "without") {
          items[step - 1].classList.add("active");
          if (!(step === steps.length - 1)) {
            items[step + 1].style.border = "1px solid grey";
            items[5].style.backgroundColor = "grey";
          }
        } else {
          if (step == 3 && projectItem.coils !== "without") {
            items[3].style.backgroundColor = theme.palette.secondary.main;
          }
          if (step == 4 && !shouldSkipHeatRecovery) {
            items[4].style.backgroundColor = theme.palette.secondary.main;
          }
          if (step == 5 && projectItem.coils !== "without") {
            items[5].style.backgroundColor = theme.palette.secondary.main;
          }
          items[step - 1].classList.add("active");
          if (!(step === steps.length - 1)) {
            items[step].style.border = "1px solid grey";
          }
        }
      }
    } catch (err) {
      console.log(err);
    }
    // items[step]
  }

  useEffect(() => {
    window.scrollTo(0, 0);
    let item = document.querySelector(".sidebar_item");
    item.style.border = "1px solid grey";
  }, []);

  const handleValidationError = (errorStep) => {
    window.scrollTo(0, 0);
    setStep(errorStep);
    let items = document.querySelectorAll(".sidebar_item");
    for (let i = 0; i < items.length; i++) {
      const element = items[i];
      element.style.border = "none";
    }
    items[errorStep - 1].style.border = "1px solid grey";
  };

  const handleBack = () => {
    window.scrollTo(0, 0);
    // Get baseUnitType for this scope
    const baseUnitTypeForBack = params?.unitType
      ? parseInt(params.unitType, 10)
      : null;
    const shouldSkipHeatRecoveryForBack = baseUnitTypeForBack === 1; // Compact:Prime

    setStep((prevStep) => {
      let items = document.querySelectorAll(".sidebar_item");

      // Clear all borders first
      for (let i = 0; i < items.length; i++) {
        const element = items[i];
        element.style.border = "none";
      }

      if (projectItem.coils === "without" && step === 6) {
        // If coils is "without", we skip Coils step (step 5)
        // So going back from Filtering (step 6) should go to Heat Recovery (step 4) or Operating Point (step 3)
        const targetStep = prevStep - 2;
        const targetIndex = targetStep - 1;
        if (targetIndex >= 0 && targetIndex < items.length) {
          items[targetIndex].style.border = "1px solid grey";
        }
        return targetStep;
      } else {
        const targetStep = prevStep - 1;
        const targetIndex = targetStep - 1;
        if (targetIndex >= 0 && targetIndex < items.length) {
          items[targetIndex].style.border = "1px solid grey";
        }
        return targetStep;
      }
    });

    // let items = document.querySelectorAll(".sidebar_item");
    // for (let i = 0; i < items.length; i++) {
    //   const element = items[i];
    //   element.style.border = "none";
    // }
    // items[step - 2].style.border = "1px solid grey";
  };

  const handleReset = () => {
    setStep(1);
  };

  useEffect(() => {
    if (location.state !== null && location.state.editing === true)
      setEditing(true);
  }, []);

  useEffect(() => {
    if (isEditing) {
      getProjectItem(location.state.data)
        .then((res) => {
          dispatch({
            type: "uploadDataForEdit",
            payload: res.data,
          });
          setInitialValues({
            ...res.data,
            winterOutdoorTemperature: res.data.winterOutdoorTemperature,
          });
          if (res.data.buildingType === "None") {
            setAirFlowCalc(true);
          }

          getProject(res.data.projectId).then((res) => {
            dispatch({
              type: "updateProject",
              payload: {
                name: "winterOutdoorHumidityRatio",
                value: res.data.winterOutdoorHumidityRatio,
                //value: res.data.item1.winterOutdoorHumidityRatio,
              },
            });
          });
        })
        .catch((err) => {
          snackbarDispatch(
            setSnackbar(true, "error", t("snackbar.fetchingDataError"))
          );
        });
    } else {
      //handleLnguage();
      //dispatch({ type: "setProjectId", payload: projectId });

      getProject(projectId)
        .then((response) => {
          if (response.statusCode >= 200 && response.statusCode < 300) {
            let operationPointData = {
              id: projectId,
              data: response.data, //.item1,
            };

            dispatch({
              type: "updateOperationPoint",
              payload: operationPointData,
            });
            setInitialValues({
              ...response.data,
              winterOutdoorTemperature: response.data.winterOutdoorTemperature,
            });
            getProjectLocationConditions(
              response.data.latitude,
              response.data.longitude
            ).then((res) => {
              if (res.statusCode >= 200 && res.statusCode < 300) {
                dispatch({
                  type: "updateDesignData",
                  payload: {
                    designDryBulbTemperature:
                      res.data.data.basicDesignConditions.summerDesignConditions
                        .summerOutdoorTemperature,
                    designWetBulbTemperature:
                      res.data.data.basicDesignConditions.summerDesignConditions
                        .summerWetBulbTemperature,
                    designDewPointTemperature:
                      res.data.data.basicDesignConditions.summerDesignConditions
                        .summerDewPointTemperature,
                    designWinterOutdoorTemperature:
                      res.data.data.basicDesignConditions.winterDesignConditions
                        .winterOutdoorTemperature,
                  },
                });
              }
            });
          } else {
            snackbarDispatch(
              setSnackbar(true, "error", t("snackbar.fetchingDataError"))
            );
          }
        })
        .catch((err) => {
          snackbarDispatch(
            setSnackbar(true, "error", t("snackbar.fetchingDataError"))
          );
        });
    }
  }, [projectId, isEditing]);
  const theme = useTheme();
  //popover
  const [anchorEl1, setAnchorEl1] = React.useState(null);
  const [anchorEl2, setAnchorEl2] = React.useState(null);
  const [anchorEl3, setAnchorEl3] = React.useState(null);
  const [anchorEl4, setAnchorEl4] = React.useState(null);
  const [anchorEl5, setAnchorEl5] = React.useState(null);
  const [anchorEl6, setAnchorEl6] = React.useState(null);
  const [anchorEl7, setAnchorEl7] = React.useState(null);
  const [anchorEl8, setAnchorEl8] = React.useState(null);
  const handlePopoverOpen1 = (event) => {
    setAnchorEl1(event.currentTarget);
  };
  const handlePopoverOpen2 = (event) => {
    setAnchorEl2(event.currentTarget);
  };
  const handlePopoverOpen3 = (event) => {
    setAnchorEl3(event.currentTarget);
  };
  const handlePopoverOpen4 = (event) => {
    setAnchorEl4(event.currentTarget);
  };
  const handlePopoverOpen5 = (event) => {
    setAnchorEl5(event.currentTarget);
  };
  const handlePopoverOpen6 = (event) => {
    setAnchorEl6(event.currentTarget);
  };
  const handlePopoverOpen7 = (event) => {
    setAnchorEl7(event.currentTarget);
  };
  const handlePopoverOpen8 = (event) => {
    setAnchorEl8(event.currentTarget);
  };
  const handlePopoverClose1 = () => {
    setAnchorEl1(null);
  };
  const handlePopoverClose2 = () => {
    setAnchorEl2(null);
  };
  const handlePopoverClose3 = () => {
    setAnchorEl3(null);
  };
  const handlePopoverClose4 = () => {
    setAnchorEl4(null);
  };
  const handlePopoverClose5 = () => {
    setAnchorEl5(null);
  };
  const handlePopoverClose6 = () => {
    setAnchorEl6(null);
  };
  const handlePopoverClose7 = () => {
    setAnchorEl7(null);
  };
  const handlePopoverClose8 = () => {
    setAnchorEl8(null);
  };
  const openPopover1 = Boolean(anchorEl1);
  const openPopover2 = Boolean(anchorEl2);
  const openPopover3 = Boolean(anchorEl3);
  const openPopover4 = Boolean(anchorEl4);
  const openPopover5 = Boolean(anchorEl5);
  const openPopover6 = Boolean(anchorEl6);
  const openPopover7 = Boolean(anchorEl7);
  const openPopover8 = Boolean(anchorEl8);

  console.log(projectItem.coils);

  return (
    <Box sx={{ display: "flex", minHeight: "70vh" }}>
      <CssBaseline />
      <Drawer
        sx={{ display: { xs: "none", md: "block" } }}
        variant="permanent"
        open={isSmallScreen ? !open : open}
      >
        <Toolbar
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            px: [1],
            color: theme.palette.primary.main,
          }}
        >
          <IconButton onClick={toggleDrawer}>
            {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
        </Toolbar>
        <ListItem
          component="nav"
          sx={{
            display: "block",
            padding: 0,
            color: theme.palette.primary.main,
          }}
        >
          {
            <React.Fragment>
              <ListItemButton
                className="sidebar_item active"
                onClick={(e) => {
                  if (active1 || checked1) {
                    window.scrollTo(0, 0);
                    //changeStep(1);
                    setStep(1);
                    let items = document.querySelectorAll(".sidebar_item");
                    for (let i = 0; i < items.length; i++) {
                      const element = items[i];
                      // element.classList.remove("active");
                      element.style.border = "none";
                    }
                    const parent = e.target.closest(".sidebar_item");
                    parent.style.border = "1px solid grey";
                  }
                }}
                sx={{
                  background: active1
                    ? theme.palette.secondary.main
                    : theme.palette.primary.light,
                  "&:hover": {
                    backgroundColor: active1
                      ? theme.palette.secondary.main
                      : theme.palette.primary.light,
                  },
                }}
              >
                <ListItemIcon>
                  <AssignmentIcon
                    aria-owns={openPopover1 ? "mouse-over-popover" : undefined}
                    aria-haspopup="true"
                    onMouseEnter={handlePopoverOpen1}
                    onMouseLeave={handlePopoverClose1}
                    sx={{ color: theme.palette.primary.main }}
                  />
                  <Popover
                    disableScrollLock={true}
                    id="mouse-over-popover"
                    sx={{
                      pointerEvents: "none",
                    }}
                    open={openPopover1}
                    anchorEl={anchorEl1}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "left",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "left",
                    }}
                    onClose={handlePopoverClose1}
                    disableRestoreFocus
                  >
                    <Typography sx={{ p: 1 }}>{t("mainform.info")}</Typography>
                  </Popover>
                </ListItemIcon>
                <ListItemText
                  primary={t("mainform.info")}
                  sx={{ color: theme.palette.primary.main }}
                />
              </ListItemButton>
              <ListItemButton
                className="sidebar_item active"
                onClick={(e) => {
                  if (step < 2) {
                    return;
                  } else if (active2 || checked2) {
                    window.scrollTo(0, 0);
                    //changeStep(2);
                    setStep(2);
                    let items = document.querySelectorAll(".sidebar_item");
                    for (let i = 0; i < items.length; i++) {
                      const element = items[i];
                      // element.classList.remove("active");
                      element.style.border = "none";
                    }
                    const parent = e.target.closest(".sidebar_item");
                    parent.style.border = "1px solid grey";
                  }
                }}
                sx={{
                  background: active2
                    ? theme.palette.secondary.main
                    : theme.palette.primary.light,
                  "&:hover": {
                    backgroundColor: active2
                      ? theme.palette.secondary.main
                      : theme.palette.primary.light,
                  },
                }}
              >
                <ListItemIcon>
                  <LibraryAddCheckIcon
                    aria-owns={openPopover2 ? "mouse-over-popover" : undefined}
                    aria-haspopup="true"
                    onMouseEnter={handlePopoverOpen2}
                    onMouseLeave={handlePopoverClose2}
                    sx={{ color: theme.palette.primary.main }}
                  />
                  <Popover
                    disableScrollLock={true}
                    id="mouse-over-popover"
                    sx={{
                      pointerEvents: "none",
                    }}
                    open={openPopover2}
                    anchorEl={anchorEl2}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "left",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "left",
                    }}
                    onClose={handlePopoverClose2}
                    disableRestoreFocus
                  >
                    <Typography sx={{ p: 1 }}>
                      {t("mainform.typeOfUnit")}
                    </Typography>
                  </Popover>
                </ListItemIcon>
                <ListItemText primary={t("mainform.typeOfUnit")} />
              </ListItemButton>
              <ListItemButton
                className="sidebar_item active"
                onClick={(e) => {
                  if (step < 3) {
                    return;
                  } else if (active3 || checked3) {
                    window.scrollTo(0, 0);
                    //changeStep(3);
                    setStep(3);
                    let items = document.querySelectorAll(".sidebar_item");
                    for (let i = 0; i < items.length; i++) {
                      const element = items[i];
                      // element.classList.remove("active");
                      element.style.border = "none";
                    }
                    const parent = e.target.closest(".sidebar_item");
                    parent.style.border = "1px solid grey";
                  }
                }}
                sx={{
                  background: active3
                    ? theme.palette.secondary.main
                    : theme.palette.primary.light,
                  "&:hover": {
                    backgroundColor: active3
                      ? theme.palette.secondary.main
                      : theme.palette.primary.light,
                  },
                }}
              >
                <ListItemIcon>
                  <GpsFixedIcon
                    aria-owns={openPopover3 ? "mouse-over-popover" : undefined}
                    aria-haspopup="true"
                    onMouseEnter={handlePopoverOpen3}
                    onMouseLeave={handlePopoverClose3}
                    sx={{ color: theme.palette.primary.main }}
                  />
                  <Popover
                    disableScrollLock={true}
                    id="mouse-over-popover"
                    sx={{
                      pointerEvents: "none",
                    }}
                    open={openPopover3}
                    anchorEl={anchorEl3}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "left",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "left",
                    }}
                    onClose={handlePopoverClose3}
                    disableRestoreFocus
                  >
                    <Typography sx={{ p: 1 }}>
                      {t("mainform.operatingpoint")}
                    </Typography>
                  </Popover>
                </ListItemIcon>
                <ListItemText primary={t("mainform.operatingpoint")} />
              </ListItemButton>

              {/* Heat Recovery tab - only for Energy:Prime and Aqua:Prime */}
              {params?.unitType && parseInt(params.unitType, 10) !== 1 && (
                <ListItemButton
                  className="sidebar_item active"
                  onClick={(e) => {
                    if (step < 4) {
                      return;
                    } else if (active4 || checked4) {
                      window.scrollTo(0, 0);
                      setStep(4);
                      let items = document.querySelectorAll(".sidebar_item");
                      for (let i = 0; i < items.length; i++) {
                        const element = items[i];
                        element.style.border = "none";
                      }
                      const parent = e.target.closest(".sidebar_item");
                      parent.style.border = "1px solid grey";
                    }
                  }}
                  sx={{
                    background: active4
                      ? theme.palette.secondary.main
                      : theme.palette.primary.light,
                    "&:hover": {
                      backgroundColor: active4
                        ? theme.palette.secondary.main
                        : theme.palette.primary.light,
                    },
                  }}
                >
                  <ListItemIcon>
                    <HeatPumpIcon
                      aria-owns={
                        openPopover4 ? "mouse-over-popover" : undefined
                      }
                      aria-haspopup="true"
                      onMouseEnter={handlePopoverOpen4}
                      onMouseLeave={handlePopoverClose4}
                      sx={{ color: theme.palette.primary.main }}
                    />
                    <Popover
                      disableScrollLock={true}
                      id="mouse-over-popover"
                      sx={{
                        pointerEvents: "none",
                      }}
                      open={openPopover4}
                      anchorEl={anchorEl4}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "left",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "left",
                      }}
                      onClose={handlePopoverClose4}
                      disableRestoreFocus
                    >
                      <Typography sx={{ p: 1 }}>
                        {t("mainform.heatRecovery") || "Heat Recovery"}
                      </Typography>
                    </Popover>
                  </ListItemIcon>
                  <ListItemText
                    primary={t("mainform.heatRecovery") || "Heat Recovery"}
                  />
                </ListItemButton>
              )}

              <ListItemButton
                className="sidebar_item active"
                onClick={(e) => {
                  if (step < 5) {
                    return;
                  } else if (
                    (active5 || checked5) &&
                    projectItem.coils !== "without"
                  ) {
                    window.scrollTo(0, 0);
                    //changeStep(5);
                    setStep(5);
                    let items = document.querySelectorAll(".sidebar_item");
                    for (let i = 0; i < items.length; i++) {
                      const element = items[i];
                      // element.classList.remove("active");
                      element.style.border = "none";
                    }
                    const parent = e.target.closest(".sidebar_item");
                    parent.style.border = "1px solid grey";
                  }
                }}
                sx={{
                  background: active4
                    ? theme.palette.secondary.main
                    : theme.palette.primary.light,
                  "&:hover": {
                    backgroundColor: active4
                      ? theme.palette.secondary.main
                      : theme.palette.primary.light,
                  },
                }}
              >
                <ListItemIcon>
                  <AnimationIcon
                    aria-owns={openPopover5 ? "mouse-over-popover" : undefined}
                    aria-haspopup="true"
                    onMouseEnter={handlePopoverOpen5}
                    onMouseLeave={handlePopoverClose5}
                    sx={{ color: theme.palette.primary.dark }}
                  />
                  <Popover
                    disableScrollLock={true}
                    id="mouse-over-popover"
                    sx={{
                      pointerEvents: "none",
                    }}
                    open={openPopover5}
                    anchorEl={anchorEl5}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "left",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "left",
                    }}
                    onClose={handlePopoverClose5}
                    disableRestoreFocus
                  >
                    <Typography sx={{ p: 1 }}>
                      {t("mainform.steps.coils")}
                    </Typography>
                  </Popover>
                </ListItemIcon>
                {/* <ListItemText primary={t("mainform.filtering")} /> */}
                <ListItemText primary={t("mainform.steps.coils")} />
              </ListItemButton>

              <ListItemButton
                className="sidebar_item active"
                onClick={(e) => {
                  if (step < 5) {
                    return;
                  } else if (active5 || checked5) {
                    window.scrollTo(0, 0);
                    //changeStep(5);
                    setStep(5);
                    let items = document.querySelectorAll(".sidebar_item");
                    for (let i = 0; i < items.length; i++) {
                      const element = items[i];
                      // element.classList.remove("active");
                      element.style.border = "none";
                    }
                    const parent = e.target.closest(".sidebar_item");
                    parent.style.border = "1px solid grey";
                  }
                }}
                sx={{
                  background: active5
                    ? theme.palette.secondary.main
                    : theme.palette.primary.light,
                  "&:hover": {
                    backgroundColor: active5
                      ? theme.palette.secondary.main
                      : theme.palette.primary.light,
                  },
                }}
              >
                <ListItemIcon>
                  <BlurLinearIcon
                    aria-owns={openPopover5 ? "mouse-over-popover" : undefined}
                    aria-haspopup="true"
                    onMouseEnter={handlePopoverOpen5}
                    onMouseLeave={handlePopoverClose5}
                    sx={{ color: theme.palette.primary.dark }}
                  />
                  <Popover
                    disableScrollLock={true}
                    id="mouse-over-popover"
                    sx={{
                      pointerEvents: "none",
                    }}
                    open={openPopover5}
                    anchorEl={anchorEl5}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "left",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "left",
                    }}
                    onClose={handlePopoverClose6}
                    disableRestoreFocus
                  >
                    <Typography sx={{ p: 1 }}>
                      {t("mainform.filtering")}
                    </Typography>
                  </Popover>
                </ListItemIcon>
                <ListItemText primary={t("mainform.filtering")} />
              </ListItemButton>
              <ListItemButton
                className="sidebar_item active"
                onClick={(e) => {
                  if (step < 7) {
                    return;
                  } else if (active7 || checked7) {
                    window.scrollTo(0, 0);
                    // changeStep(7);
                    setStep(7);
                    let items = document.querySelectorAll(".sidebar_item");
                    for (let i = 0; i < items.length; i++) {
                      const element = items[i];
                      // element.classList.remove("active");
                      element.style.border = "none";
                    }
                    const parent = e.target.closest(".sidebar_item");
                    parent.style.border = "1px solid grey";
                  }
                }}
                sx={{
                  background: active6
                    ? theme.palette.secondary.main
                    : theme.palette.primary.light,
                  "&:hover": {
                    backgroundColor: active6
                      ? theme.palette.secondary.main
                      : theme.palette.primary.light,
                  },
                }}
              >
                <ListItemIcon>
                  <HeatPumpIcon
                    aria-owns={openPopover6 ? "mouse-over-popover" : undefined}
                    aria-haspopup="true"
                    onMouseEnter={handlePopoverOpen6}
                    onMouseLeave={handlePopoverClose6}
                    sx={{ color: theme.palette.primary.main }}
                  />
                  <Popover
                    disableScrollLock={true}
                    id="mouse-over-popover"
                    sx={{
                      pointerEvents: "none",
                    }}
                    open={openPopover6}
                    anchorEl={anchorEl6}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "left",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "left",
                    }}
                    onClose={handlePopoverClose7}
                    disableRestoreFocus
                  >
                    <Typography sx={{ p: 1 }}>{t("mainform.fan")}</Typography>
                  </Popover>
                </ListItemIcon>
                <ListItemText primary={t("mainform.fan")} />
              </ListItemButton>
              <ListItemButton
                className="sidebar_item active"
                onClick={(e) => {
                  if (step < 8) {
                    return;
                  } else if (active8 || checked8) {
                    window.scrollTo(0, 0);
                    //  changeStep(8);
                    setStep(8);
                    let items = document.querySelectorAll(".sidebar_item");
                    for (let i = 0; i < items.length; i++) {
                      const element = items[i];
                      // element.classList.remove("active");
                      element.style.border = "none";
                    }
                    const parent = e.target.closest(".sidebar_item");
                    parent.style.border = "1px solid grey";
                  }
                }}
                sx={{
                  background: active7
                    ? theme.palette.secondary.main
                    : theme.palette.primary.light,
                  // "&:hover": {
                  //   backgroundColor: theme.palette.secondary.main,
                  // },
                }}
              >
                <ListItemIcon>
                  <CalculateIcon
                    aria-owns={openPopover7 ? "mouse-over-popover" : undefined}
                    aria-haspopup="true"
                    onMouseEnter={handlePopoverOpen7}
                    onMouseLeave={handlePopoverClose7}
                    sx={{ color: theme.palette.primary.main }}
                  />
                  <Popover
                    disableScrollLock={true}
                    id="mouse-over-popover"
                    sx={{
                      pointerEvents: "none",
                    }}
                    open={openPopover7}
                    anchorEl={anchorEl7}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "left",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "left",
                    }}
                    onClose={handlePopoverClose8}
                    disableRestoreFocus
                  >
                    <Typography sx={{ p: 1 }}>
                      {t("mainform.calculation")}
                    </Typography>
                  </Popover>
                </ListItemIcon>
                <ListItemText primary={t("mainform.calculation")} />
              </ListItemButton>
            </React.Fragment>
          }
        </ListItem>
      </Drawer>
      <Box
        component="main"
        sx={{
          backgroundColor: (theme) =>
            theme.palette.mode === "light"
              ? theme.palette.grey[100]
              : theme.palette.grey[900],
          flexGrow: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
        }}
      >
        {/* <section className="content"> */}
        <Box
          sx={{
            width: "90%",
            // ml: "5%",
            // mr: "5%",
            minHeight: "90vh",
            display: "flex",
            alignItems: "flex-start",
            // justifyContent: "flex-start",
          }}
        >
          {/* odavde */}

          {/*<section className="content_body">*/}
          <div className="mainform-wrapper" style={{ width: "100%" }}>
            {step === steps.length ? (
              <React.Fragment>
                <Typography
                  sx={{ mt: 2, mb: 1, color: theme.palette.primary.main }}
                >
                  {t("mainform.allsteps")}
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
                  <Box sx={{ flex: "1 1 auto" }} />
                  <Button onClick={handleReset}>
                    <Button
                      onClick={() => setStep(1)}
                      sx={{ color: theme.palette.primary.main }}
                    >
                      {t("mainform.reset")}
                    </Button>
                  </Button>
                </Box>
              </React.Fragment>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  margin: "15px 0",
                }}
              >
                <Button
                  disabled={step === 1}
                  onClick={() => {
                    handleBack();
                    // changeStep(step);
                  }}
                  sx={{
                    mr: 1,
                    width: "80px",
                    // color: "#373437",
                    color: theme.palette.primary.light,
                    backgroundColor: theme.palette.secondary.main,
                    transition: "all 0.5s",
                    "&:hover": {
                      backgroundColor: theme.palette.secondary.main,
                      transform: "scale(1.05)",
                    },
                  }}
                >
                  {t("mainform.back")}
                </Button>
                <Box sx={{ flex: "1 1 auto" }} />
                <Button
                  // className={step === steps.length - 1 ? "disp-none" : ""}
                  onClick={() => {
                    if (step === steps.length - 1) {
                      onSubmit();
                    } else if (step === 1) {
                      calculateAirFlow();
                      handleNext();
                      // changeStep(step);
                    } else {
                      handleNext();
                      //   changeStep(step);
                    }
                  }}
                  sx={
                    step === steps.length - 1
                      ? { display: "none" }
                      : {
                          // color: "#373437",
                          color: theme.palette.primary.light,
                          backgroundColor: theme.palette.secondary.main,
                          width: "80px",
                          transition: "all 0.5s",
                          "&:hover": {
                            backgroundColor: theme.palette.secondary.main,
                            transform: "scale(1.05)",
                          },
                        }
                  }
                >
                  {t("mainform.next")}
                </Button>
              </Box>
            )}
            <Box className="div-border">
              {/* <form action=""> */}
              <ProjectInformation
                setSnackbar={setSnackbar}
                snackbarDispatch={snackbarDispatch}
                areMandatoryStepFieldsFilled={areMandatoryStepFieldsFilled}
                projectItem={projectItem}
                calculateAirFlow={calculateAirFlow}
                active1={active1}
                //ref={childRef}
                airFlowCalculatorFields={airFlowCalculatorFields}
                airFlowDirectInputFields={airFlowDirectInputFields}
                setAirFlowCalc={setAirFlowCalc}
                airFlowCalc={airFlowCalc}
                isEditing={isEditing}
                projName={projectItem.projName}
                currentStep={step}
                handleChange={handleProjectChange}
                area={projectItem.area}
                height={projectItem.height}
                buildingType={projectItem.buildingType}
                changeRate={projectItem.changeRate}
                dropSupply={projectItem.dropSupply}
                flowSupply={projectItem.flowSupply}
                flowReturn={projectItem.flowReturn}
                dropReturn={projectItem.dropReturn}
              />
              <TypeOfUnit
                currentStep={step}
                handleChange={handleProjectChange}
                installation={projectItem.installation}
                unitType={projectItem.unitType}
                coils={projectItem.coils}
                heatRecovery={projectItem.heatRecovery}
                controls={projectItem.controls}
                unitDivision={projectItem.unitDivision}
                unitTypes={unitTypes}
                sizeOfUnit={projectItem.sizeOfUnit}
                heatRecoveryPlateType={projectItem.heatRecoveryPlateType}
                selectionExplanation={selectionExplanation}
                airflowDirection={projectItem.airflowDirection}
                ecoDesign={projectItem.ecoDesign}
                casingQuality={projectItem.casingQuality}
                baseUnitType={
                  params?.unitType ? parseInt(params.unitType, 10) : null
                }
              ></TypeOfUnit>
              <OperatingPoint1
                currentStep={step}
                handleChange={handleProjectChange}
                projectId={projectItem.projId}
                coils={projectItem.coils}
                summerOutdoorTemperature={projectItem.summerOutdoorTemperature}
                summerWetBulbTemperature={projectItem.summerWetBulbTemperature}
                summerDewPointTemperature={
                  projectItem.summerDewPointTemperature
                }
                summerOutdoorHumidity={projectItem.summerOutdoorHumidity}
                summerIndoorTemperature={projectItem.summerIndoorTemperature}
                summerIndoorHumidity={projectItem.summerIndoorHumidity}
                winterOutdoorTemperature={projectItem.winterOutdoorTemperature}
                winterOutdoorHumidity={projectItem.winterOutdoorHumidity}
                winterIndoorTemperature={projectItem.winterIndoorTemperature}
                winterIndoorHumidity={projectItem.winterIndoorHumidity}
                elevation={projectItem.elevation}
                heaterType={projectItem.heaterType}
                heaterConcentration={projectItem.heaterConcentration}
                heaterTemperatureIn={projectItem.heaterTemperatureIn}
                waterInputParamValueHeater={
                  projectItem.waterInputParamValueHeater
                }
                coolerType={projectItem.coolerType}
                coolerConcentration={projectItem.coolerConcentration}
                coolerTemperatureIn={projectItem.coolerTemperatureIn}
                waterInputParamValueCooler={
                  projectItem.waterInputParamValueCooler
                }
                heatRecovery={projectItem.heatRecovery}
                heatRecoveryPlateType={projectItem.heatRecoveryPlateType}
                airflowDirection={projectItem.airflowDirection}
              ></OperatingPoint1>
              <HeatRecovery
                currentStep={step}
                summerOutdoorTemperature={projectItem.summerOutdoorTemperature}
                summerOutdoorHumidity={projectItem.summerOutdoorHumidity}
                winterOutdoorTemperature={projectItem.winterOutdoorTemperature}
                winterOutdoorHumidity={projectItem.winterOutdoorHumidity}
                summerIndoorTemperature={projectItem.summerIndoorTemperature}
                summerIndoorHumidity={projectItem.summerIndoorHumidity}
                winterIndoorTemperature={projectItem.winterIndoorTemperature}
                winterIndoorHumidity={projectItem.winterIndoorHumidity}
                summerRecCalculation={projectItem.summerRecCalculation}
                winterRecCalculation={projectItem.winterRecCalculation}
              ></HeatRecovery>
              <Coils
                currentStep={step}
                handleChange={handleProjectChange}
                coils={projectItem.coils}
                baseUnitType={
                  params?.unitType ? parseInt(params.unitType, 10) : null
                }
                unitType={projectItem.unitType}
                heaterType={projectItem.heaterType}
                heaterConcentration={projectItem.heaterConcentration}
                heaterTemperatureIn={projectItem.heaterTemperatureIn}
                waterInputParamValueHeater={
                  projectItem.waterInputParamValueHeater
                }
                coolerType={projectItem.coolerType}
                coolerConcentration={projectItem.coolerConcentration}
                coolerTemperatureIn={projectItem.coolerTemperatureIn}
                waterInputParamValueCooler={
                  projectItem.waterInputParamValueCooler
                }
                winterSupplyTemperatureState={
                  projectItem.winterSupplyTemperatureState
                }
                summerSupplyTemperatureState={
                  projectItem.summerSupplyTemperatureState
                }
                dryBulbTemperatureCelsius1={
                  projectItem.dryBulbTemperatureCelsius1
                }
                coilInputTemperatureCooler={
                  projectItem.coilInputTemperatureCooler
                }
                coilInputTemperatureHeater={
                  projectItem.coilInputTemperatureHeater
                }
                coilInputHumidityCooler={projectItem.coilInputHumidityCooler}
                coilInputHumidityHeater={projectItem.coilInputHumidityHeater}
                airTempInCool={airTempInCool}
                setAirTempInCool={setAirTempInCool}
                airTempInHeat={airTempInHeat}
                setAirTempInHeat={setAirTempInHeat}
                setStep={setStep}
                heaterAirInputParamValue={projectItem.heaterAirInputParamValue}
                heaterAirInputParam={projectItem.heaterAirInputParam}
                heaterWaterInputParamValue={
                  projectItem.heaterWaterInputParamValue
                }
                heaterWaterInputParam={projectItem.heaterWaterInputParam}
                coolerAirInputParamValue={projectItem.coolerAirInputParamValue}
                coolerAirInputParam={projectItem.coolerAirInputParam}
                coolerWaterInputParamValue={
                  projectItem.coolerWaterInputParamValue
                }
                coolerWaterInputParam={projectItem.coolerWaterInputParam}
              ></Coils>
              <Filtering
                currentStep={step}
                handleChange={handleProjectChange}
                doubleStageFiltering={projectItem.doubleStageFiltering}
                filteringAdvancedSettings={
                  projectItem.filteringAdvancedSettings
                }
                fineFilterType={projectItem.fineFilterType}
                fineFilterClassSupply={projectItem.fineFilterClassSupply}
                fineFilterClassReturn={projectItem.fineFilterClassReturn}
              ></Filtering>

              <Fan
                currentStep={step}
                handleChange={handleProjectChange}
                fanAdvancedSettings={projectItem.fanAdvancedSettings}
                fanType={projectItem.fanType}
              ></Fan>
              <Calculation
                airFlowCalc={airFlowCalc}
                currentStep={step}
                onSubmit={onSubmit}
                area={projectItem.area}
                height={projectItem.height}
                buildingType={projectItem.buildingType}
                changeRate={projectItem.changeRate}
                dropSupply={projectItem.dropSupply}
                flowSupply={projectItem.flowSupply}
                flowReturn={projectItem.flowReturn}
                dropReturn={projectItem.dropReturn}
                ecoDesign={projectItem.ecoDesign}
                casingQuality={projectItem.casingQuality}
                installation={projectItem.installation}
                unitType={projectItem.unitType}
                heatRecovery={projectItem.heatRecovery}
                //controls={projectItem.controls}
                sizeOfUnit={projectItem.sizeOfUnit}
                heatRecoveryPlateType={projectItem.heatRecoveryPlateType}
                unitDivision={projectItem.unitDivision}
                summerOutdoorTemperature={projectItem.summerOutdoorTemperature}
                summerWetBulbTemperature={projectItem.summerWetBulbTemperature}
                summerDewPointTemperature={
                  projectItem.summerDewPointTemperature
                }
                summerOutdoorHumidity={projectItem.summerOutdoorHumidity}
                summerIndoorTemperature={projectItem.summerIndoorTemperature}
                summerIndoorHumidity={projectItem.summerIndoorHumidity}
                winterOutdoorTemperature={projectItem.winterOutdoorTemperature}
                winterOutdoorHumidity={projectItem.winterOutdoorHumidity}
                elevation={projectItem.elevation}
                winterIndoorTemperature={projectItem.winterIndoorTemperature}
                winterIndoorHumidity={projectItem.winterIndoorHumidity}
                supplyAirFilter={projectItem.supplyAirFilter}
                doubleStageFiltering={projectItem.doubleStageFiltering}
                filteringAdvancedSettings={
                  projectItem.filteringAdvancedSettings
                }
                fineFilterType={projectItem.fineFilterType}
                fineFilterClassSupply={projectItem.fineFilterClassSupply}
                fineFilterClassReturn={projectItem.fineFilterClassReturn}
                fanAdvancedSettings={projectItem.fanAdvancedSettings}
                fanType={projectItem.fanType}
                coils={projectItem.coils}
                heaterType={projectItem.heaterType}
                heaterConcentration={projectItem.heaterConcentration}
                heaterTemperatureIn={projectItem.heaterTemperatureIn}
                coolerType={projectItem.coolerType}
                coolerConcentration={projectItem.coolerConcentration}
                coolerTemperatureIn={projectItem.coolerTemperatureIn}
                enableButton={fanValSuccess && coilsValSuccess}
                coolerAirInputParam={projectItem.coolerAirInputParam}
                coolerAirInputParamValue={projectItem.coolerAirInputParamValue}
                coolerWaterInputParam={projectItem.coolerWaterInputParam}
                coolerWaterInputParamValue={
                  projectItem.coolerWaterInputParamValue
                }
                heaterAirInputParam={projectItem.heaterAirInputParam}
                heaterAirInputParamValue={projectItem.heaterAirInputParamValue}
                heaterWaterInputParam={projectItem.heaterWaterInputParam}
                heaterWaterInputParamValue={
                  projectItem.heaterWaterInputParamValue
                }
              ></Calculation>

              <Backdrop
                sx={{
                  color: theme.palette.primary.light,
                  zIndex: (theme) => theme.zIndex.drawer + 1,
                }}
                open={loading}
              >
                <CircularProgress />
              </Backdrop>
            </Box>
          </div>
        </Box>
      </Box>
    </Box>
  );
}

export default MainForm;

const steps = [
  "Project successfully created",
  "Unit Information",
  "Type of unit",
  "Operating point",
  "Coils",
  "Filtering",
  "Fan",
  "Calculation",
];
